import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import '../styles/KatKarsilig.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import KatCizimPanel from '../components/KatCizimPanel';
import yonlerImg from '../images/yonler.png';

// ─── Types ────────────────────────────────────────────────────────────────────

interface Arsa {
    arsaID: number;
    takipNo: string;
    ada: string | null;
    parsel: string | null;
    il: string | null;
    ilce: string | null;
    mahalle: string | null;
    arsaMetrekare: number | null;
    arsaTutar: number | null;
    aciklama: string | null;
    aktifMi: boolean;
    muteahhitler: Muteahhit[];
    ortaklar: ArsaOrtak[];
    bloklar: Blok[];
    cepheler: Cephe[];
    bolumler: Bolum[];
}

interface Muteahhit {
    muteahhitID: number;
    arsaID: number;
    adSoyad: string;
    telefon: string | null;
    eposta: string | null;
    vergiNo: string | null;
    not: string | null;
    hisseMetre: number | null;
    payYuzde: number;
    toplamPayYuzde: number;
    payTL: number | null;
    aldigiPayTL: number | null;
}

interface ArsaOrtak {
    ortakID: number;
    arsaID: number;
    adSoyad: string;
    telefon: string | null;
    eposta: string | null;
    tcKimlik: string | null;
    hisseMetre: number | null;
    hisseOrani: number;
    not: string | null;
    payTL: number | null;
    aldigiPayTL: number | null;
}

interface Blok {
    blokID: number;
    arsaID: number;
    blokAdi: string;
    katSayisi: number | null;
    en: number | null;
    boy: number | null;
    aciklama: string | null;
    katlar: Kat[];
}

interface Kat {
    katID: number;
    blokID: number;
    katAdi: string;
    siraNo: number;
    en: number | null;
    boy: number | null;
}

interface Cephe {
    cepheID: number;
    arsaID: number;
    cepheAdi: string;
    yon: string | null;
    aciklama: string | null;
}

interface Bolum {
    bolumID: number;
    arsaID: number;
    blokID: number | null;
    katID: number | null;
    cephe1ID: number | null;
    cephe2ID: number | null;
    cephe3ID: number | null;
    cephe4ID: number | null;
    bolumAdi: string;
    bolumTipi: string | null;
    turu: string | null;
    m2: number;
    fiyat: number;
    birimFiyat: number;
    paylasılanAlan: number;
    not: string | null;
    xVal: number | null;
    yVal: number | null;
    blok: { blokAdi: string } | null;
    kat: { katAdi: string } | null;
    cephe1: { cepheAdi: string } | null;
    cephe2: { cepheAdi: string } | null;
    cephe3: { cepheAdi: string } | null;
    cephe4: { cepheAdi: string } | null;
    paylasimlar: Paylasim[];
}

interface Paylasim {
    paylasimID: number;
    bolumID: number;
    ortakID: number | null;
    muteahhitID: number | null;
    oranYuzde: number;
    aciklamaNot: string | null;
    ortak: { adSoyad: string } | null;
    muteahhit: { adSoyad: string } | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(val: number | null | undefined) {
    if (val == null) return '–';
    return val.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

type TabKey = 'genel' | 'muteahhitler' | 'ortaklar' | 'bloklar' | 'cepheler' | 'bolumler' | 'paylasimlar';

// ─── Öneri Tipleri ────────────────────────────────────────────────────────────

interface OneriKisi {
    tip: 'Ortak' | 'Muteahhit';
    id: number;
    adSoyad: string;
    payTL: number;
    kalanOncesi: number;
    oneriOran: number;
    oneriTL: number;
    kalanSonra: number;
    durum: 'tamamlandi' | 'devam';
}

interface Atanamamis {
    tip: string;
    id: number;
    adSoyad: string;
    payTL: number;
    kalanTL: number;
}

interface MevcutPaylasimOneri {
    paylasimID: number;
    adSoyad: string;
    tip: string;
    oranYuzde: number;
    payTL: number;
}

interface BolumOneri {
    bolumID: number;
    bolumAdi: string;
    bolumTipi: string | null;
    fiyat: number;
    dagitilanOran: number;
    kalanOran: number;
    kalanFiyat: number;
    atanmayanFiyat: number;
    mevcutPaylasimlar: MevcutPaylasimOneri[];
    oneriler: OneriKisi[];
}

interface PaylasimOnerileri {
    arsaId: number;
    toplamKalanTL: number;
    bolumler: BolumOneri[];
    atanamamis: Atanamamis[];
}

// ─── Component ────────────────────────────────────────────────────────────────

const KatKarsiligArsaDetay: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const arsaId = Number(id);
    const navigate = useNavigate();

    const [arsa, setArsa] = useState<Arsa | null>(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<TabKey>('bolumler');

    // ─ Modal states ─
    const [muteahhitModal, setMuteahhitModal] = useState(false);
    const [muteahhitEdit, setMuteahhitEdit] = useState<Muteahhit | null>(null);
    const [muteahhitForm, setMuteahhitForm] = useState({ adSoyad: '', telefon: '', eposta: '', vergiNo: '', not: '', hisseMetre: '', payYuzde: '' });

    const [ortakModal, setOrtakModal] = useState(false);
    const [ortakEdit, setOrtakEdit] = useState<ArsaOrtak | null>(null);
    const [ortakForm, setOrtakForm] = useState({ adSoyad: '', telefon: '', eposta: '', tcKimlik: '', hisseMetre: '', not: '' });

    const [blokModal, setBlokModal] = useState(false);
    const [blokEdit, setBlokEdit] = useState<Blok | null>(null);
    const [blokForm, setBlokForm] = useState({ blokAdi: '', katSayisi: '', en: '', boy: '', aciklama: '' });

    // inline kat ekleme: blokID -> satır görünür mü
    const [inlineKatBlokId, setInlineKatBlokId] = useState<number | null>(null);
    const [selectedBlokId, setSelectedBlokId] = useState<number | null>(null);
    const [cizimKat, setCizimKat] = useState<{ blok: Blok; kat: Kat } | null>(null);
    const [inlineKatForm, setInlineKatForm] = useState({ katAdi: '', siraNo: '', en: '', boy: '' });
    const [inlineKatSaving, setInlineKatSaving] = useState(false);

    const [katModal, setKatModal] = useState(false);
    const [katBlokId, setKatBlokId] = useState<number | null>(null);
    const [katEdit, setKatEdit] = useState<Kat | null>(null);
    const [katForm, setKatForm] = useState({ katAdi: '', siraNo: '', en: '', boy: '' });

    const [cepheModal, setCepheModal] = useState(false);
    const [cepheEdit, setCepheEdit] = useState<Cephe | null>(null);
    const [cepheForm, setCepheForm] = useState({ cepheAdi: '', yon: '', aciklama: '' });

    const [bolumModal, setBolumModal] = useState(false);
    const [bolumEdit, setBolumEdit] = useState<Bolum | null>(null);
    const [bolumForm, setBolumForm] = useState({
        blokID: '', katID: '', cephe1ID: '', cephe2ID: '', cephe3ID: '', cephe4ID: '',
        bolumAdi: '', bolumTipi: '', turu: '', m2: '', fiyat: '', birimFiyat: '', not: ''
    });
    const [turuListesi, setTuruListesi] = useState<string[]>([]);

    const [paylasimModal, setPaylasimModal] = useState(false);
    const [paylasimBolumId, setPaylasimBolumId] = useState<number | null>(null);
    const [paylasimEdit, setPaylasimEdit] = useState<Paylasim | null>(null);
    const [paylasimForm, setPaylasimForm] = useState({ ortakID: '', muteahhitID: '', oranYuzde: '', aciklamaNot: '' });

    const [oneriModal, setOneriModal] = useState(false);
    const [oneriData, setOneriData] = useState<PaylasimOnerileri | null>(null);
    const [oneriLoading, setOneriLoading] = useState(false);
    const [showIslemler, setShowIslemler] = useState(false);
    const [showIndir, setShowIndir] = useState(false);
    const [showBlokRaporModal, setShowBlokRaporModal] = useState(false);
    const [blokRaporBlokId, setBlokRaporBlokId] = useState<number | null>(null);
    const [showBlokRaporPayliModal, setShowBlokRaporPayliModal] = useState(false);
    const [blokRaporPayliBlokId, setBlokRaporPayliBlokId] = useState<number | null>(null);
    const [showVaziyetModal, setShowVaziyetModal] = useState(false);
    const [vaziyetBlokId, setVaziyetBlokId] = useState<number | null>(null);

    // ─ Bölüm görünüm & filtre durumu ─
    const [bolumView, setBolumView] = useState<'liste' | 'tablo'>('liste');
    const [bolumFilterTip, setBolumFilterTip] = useState('');
    const [bolumFilterTuru, setBolumFilterTuru] = useState('');
    const [bolumFilterAdi, setBolumFilterAdi] = useState('');
    const [bolumFilterBlok, setBolumFilterBlok] = useState('');
    const [bolumFilterKat, setBolumFilterKat] = useState('');
    const [bolumTabloBlokId, setBolumTabloBlokId] = useState<number | null>(null);
    const [hoveredBolumID, setHoveredBolumID] = useState<number | null>(null);
    const [hoveredPayKey, setHoveredPayKey] = useState<string | null>(null);
    const [planModalKat, setPlanModalKat] = useState<{ blok: Blok; kat: Kat } | null>(null);

    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState('');

    // ─ Fetch ─
    const fetchArsa = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/KatKarsiligArsa/${arsaId}`);
            const data = res.data;
            // API null döndürebileceğinden collection'lara varsayılan değer ver
            data.muteahhitler = data.muteahhitler ?? [];
            data.ortaklar = data.ortaklar ?? [];
            data.bloklar = (data.bloklar ?? []).map((b: Blok) => ({ ...b, katlar: b.katlar ?? [] }));
            data.cepheler = data.cepheler ?? [];
            data.bolumler = (data.bolumler ?? []).map((b: Bolum) => ({ ...b, paylasimlar: b.paylasimlar ?? [] }));
            setArsa(data);
        } catch {
            alert('Arsa yüklenemedi.');
        } finally {
            setLoading(false);
        }
    }, [arsaId]);

    useEffect(() => { fetchArsa(); }, [fetchArsa]);

    useEffect(() => {
        axiosInstance.get<string[]>('/api/KatKarsiligArsa/bolumler/turu-listesi')
            .then(res => setTuruListesi(res.data))
            .catch(() => {});
    }, []);

    // ─── Müteahhit ────────────────────────────────────────────────────────────

    const openMuteahhitCreate = () => {
        setMuteahhitEdit(null);
        setMuteahhitForm({ adSoyad: '', telefon: '', eposta: '', vergiNo: '', not: '', hisseMetre: '', payYuzde: '' });
        setModalError('');
        setMuteahhitModal(true);
    };
    const openMuteahhitEdit = (m: Muteahhit) => {
        setMuteahhitEdit(m);
        setMuteahhitForm({ adSoyad: m.adSoyad, telefon: m.telefon ?? '', eposta: m.eposta ?? '', vergiNo: m.vergiNo ?? '', not: m.not ?? '', hisseMetre: m.hisseMetre !== null ? String(m.hisseMetre) : '', payYuzde: String(m.payYuzde) });
        setModalError('');
        setMuteahhitModal(true);
    };
    const saveMuteahhit = async () => {
        if (!muteahhitForm.adSoyad.trim()) { setModalError('Ad Soyad zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            const payload = { ...muteahhitForm, hisseMetre: muteahhitForm.hisseMetre !== '' ? parseFloat(muteahhitForm.hisseMetre) : null, payYuzde: parseFloat(muteahhitForm.payYuzde) || 0 };
            if (muteahhitEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/muteahhitler/${muteahhitEdit.muteahhitID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/muteahhitler`, payload);
            }
            setMuteahhitModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteMuteahhit = async (mId: number) => {
        if (!window.confirm('Müteahhiti silmek istediğinizden emin misiniz?')) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/muteahhitler/${mId}`);
        fetchArsa();
    };

    // ─── Ortak ────────────────────────────────────────────────────────────────

    const openOrtakCreate = () => {
        setOrtakEdit(null);
        setOrtakForm({ adSoyad: '', telefon: '', eposta: '', tcKimlik: '', hisseMetre: '', not: '' });
        setModalError('');
        setOrtakModal(true);
    };
    const openOrtakEdit = (o: ArsaOrtak) => {
        setOrtakEdit(o);
        setOrtakForm({ adSoyad: o.adSoyad, telefon: o.telefon ?? '', eposta: o.eposta ?? '', tcKimlik: o.tcKimlik ?? '', hisseMetre: o.hisseMetre !== null ? String(o.hisseMetre) : '', not: o.not ?? '' });
        setModalError('');
        setOrtakModal(true);
    };
    const saveOrtak = async () => {
        if (!ortakForm.adSoyad.trim()) { setModalError('Ad Soyad zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            const payload = { ...ortakForm, hisseMetre: ortakForm.hisseMetre !== '' ? parseFloat(ortakForm.hisseMetre) : null };
            if (ortakEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/ortaklar/${ortakEdit.ortakID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/ortaklar`, payload);
            }
            setOrtakModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteOrtak = async (oId: number) => {
        if (!window.confirm('Ortağı silmek istediğinizden emin misiniz?')) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/ortaklar/${oId}`);
        fetchArsa();
    };

    // ─── Blok ─────────────────────────────────────────────────────────────────

    const openBlokCreate = () => {
        setBlokEdit(null);
        setBlokForm({ blokAdi: '', katSayisi: '', en: '', boy: '', aciklama: '' });
        setModalError('');
        setBlokModal(true);
    };
    const openBlokEdit = (b: Blok) => {
        setBlokEdit(b);
        setBlokForm({ blokAdi: b.blokAdi, katSayisi: b.katSayisi !== null ? String(b.katSayisi) : '', en: b.en !== null ? String(b.en) : '', boy: b.boy !== null ? String(b.boy) : '', aciklama: b.aciklama ?? '' });
        setModalError('');
        setBlokModal(true);
    };
    const saveBlok = async () => {
        if (!blokForm.blokAdi.trim()) { setModalError('Blok Adı zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            const payload = { blokAdi: blokForm.blokAdi, katSayisi: blokForm.katSayisi !== '' ? parseInt(blokForm.katSayisi) : null, en: blokForm.en !== '' ? parseInt(blokForm.en) : null, boy: blokForm.boy !== '' ? parseInt(blokForm.boy) : null, aciklama: blokForm.aciklama };
            if (blokEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bloklar/${blokEdit.blokID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bloklar`, payload);
            }
            setBlokModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteBlok = async (bId: number) => {
        if (!window.confirm('Bloğu ve içindeki tüm katları silmek istediğinizden emin misiniz?')) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bloklar/${bId}`);
        fetchArsa();
    };

    // ─── Kat ──────────────────────────────────────────────────────────────────

    const openInlineKatAdd = (blok: Blok) => {
        const sortedKatlar = [...blok.katlar].sort((a, b) => a.siraNo - b.siraNo);
        const lastKat = sortedKatlar[sortedKatlar.length - 1];
        const nextSiraNo = lastKat ? lastKat.siraNo + 1 : 1;
        const nextKatAdi = `${nextSiraNo}. Kat`;
        setInlineKatForm({
            katAdi: nextKatAdi,
            siraNo: String(nextSiraNo),
            en: blok.en !== null ? String(blok.en) : '',
            boy: blok.boy !== null ? String(blok.boy) : '',
        });
        setInlineKatBlokId(blok.blokID);
    };

    const saveInlineKat = async (blokId: number) => {
        if (!inlineKatForm.katAdi.trim()) return;
        setInlineKatSaving(true);
        try {
            const payload = {
                katAdi: inlineKatForm.katAdi,
                siraNo: parseInt(inlineKatForm.siraNo) || 0,
                en: inlineKatForm.en !== '' ? parseInt(inlineKatForm.en) : null,
                boy: inlineKatForm.boy !== '' ? parseInt(inlineKatForm.boy) : null,
            };
            await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bloklar/${blokId}/katlar`, payload);
            setInlineKatBlokId(null);
            fetchArsa();
        } catch { }
        finally { setInlineKatSaving(false); }
    };

    const openKatEdit = (blokId: number, k: Kat) => {
        setKatBlokId(blokId);
        setKatEdit(k);
        setKatForm({ katAdi: k.katAdi, siraNo: String(k.siraNo), en: k.en !== null ? String(k.en) : '', boy: k.boy !== null ? String(k.boy) : '' });
        setModalError('');
        setKatModal(true);
    };
    const saveKat = async () => {
        if (!katForm.katAdi.trim() || katBlokId === null) { setModalError('Kat Adı zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            const payload = { katAdi: katForm.katAdi, siraNo: parseInt(katForm.siraNo) || 0, en: katForm.en !== '' ? parseInt(katForm.en) : null, boy: katForm.boy !== '' ? parseInt(katForm.boy) : null };
            await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bloklar/${katBlokId}/katlar/${katEdit!.katID}`, payload);
            setKatModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteKat = async (blokId: number, kId: number) => {
        if (!window.confirm('Katı silmek istediğinizden emin misiniz?')) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bloklar/${blokId}/katlar/${kId}`);
        fetchArsa();
    };

    // ─── Cephe ────────────────────────────────────────────────────────────────

    const openCepheCreate = () => {
        setCepheEdit(null);
        setCepheForm({ cepheAdi: '', yon: '', aciklama: '' });
        setModalError('');
        setCepheModal(true);
    };
    const openCepheEdit = (c: Cephe) => {
        setCepheEdit(c);
        setCepheForm({ cepheAdi: c.cepheAdi, yon: c.yon ?? '', aciklama: c.aciklama ?? '' });
        setModalError('');
        setCepheModal(true);
    };
    const saveCephe = async () => {
        if (!cepheForm.cepheAdi.trim()) { setModalError('Cephe Adı zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            if (cepheEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/cepheler/${cepheEdit.cepheID}`, cepheForm);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/cepheler`, cepheForm);
            }
            setCepheModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteCephe = async (cId: number) => {
        if (!window.confirm('Cepheyi silmek istediğinizden emin misiniz?')) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/cepheler/${cId}`);
        fetchArsa();
    };

    // ─── Bölüm ────────────────────────────────────────────────────────────────

    const openBolumCreate = () => {
        setBolumEdit(null);
        setBolumForm({ blokID: '', katID: '', cephe1ID: '', cephe2ID: '', cephe3ID: '', cephe4ID: '', bolumAdi: '', bolumTipi: '', turu: '', m2: '', fiyat: '', birimFiyat: '', not: '' });
        setModalError('');
        setBolumModal(true);
    };
    const openBolumEdit = (b: Bolum) => {
        setBolumEdit(b);
        setBolumForm({
            blokID: b.blokID !== null ? String(b.blokID) : '',
            katID: b.katID !== null ? String(b.katID) : '',
            cephe1ID: b.cephe1ID !== null ? String(b.cephe1ID) : '',
            cephe2ID: b.cephe2ID !== null ? String(b.cephe2ID) : '',
            cephe3ID: b.cephe3ID !== null ? String(b.cephe3ID) : '',
            cephe4ID: b.cephe4ID !== null ? String(b.cephe4ID) : '',
            bolumAdi: b.bolumAdi,
            bolumTipi: b.bolumTipi ?? '',
            turu: b.turu ?? '',
            m2: String(b.m2),
            fiyat: String(b.fiyat),
            birimFiyat: b.m2 > 0 ? String(Math.round((b.fiyat / b.m2) * 100) / 100) : String(b.birimFiyat),
            not: b.not ?? ''
        });
        setModalError('');
        setBolumModal(true);
    };
    const saveBolum = async () => {
        if (!bolumForm.bolumAdi.trim()) { setModalError('Bölüm Adı zorunludur.'); return; }
        setSaving(true); setModalError('');
        try {
            const payload = {
                blokID: bolumForm.blokID !== '' ? parseInt(bolumForm.blokID) : null,
                katID: bolumForm.katID !== '' ? parseInt(bolumForm.katID) : null,
                cephe1ID: bolumForm.cephe1ID !== '' ? parseInt(bolumForm.cephe1ID) : null,
                cephe2ID: bolumForm.cephe2ID !== '' ? parseInt(bolumForm.cephe2ID) : null,
                cephe3ID: bolumForm.cephe3ID !== '' ? parseInt(bolumForm.cephe3ID) : null,
                cephe4ID: bolumForm.cephe4ID !== '' ? parseInt(bolumForm.cephe4ID) : null,
                bolumAdi: bolumForm.bolumAdi,
                bolumTipi: bolumForm.bolumTipi || null,
                turu: bolumForm.turu || null,
                m2: parseFloat(bolumForm.m2) || 0,
                fiyat: parseFloat(bolumForm.fiyat) || 0,
                birimFiyat: parseFloat(bolumForm.birimFiyat) || 0,
                not: bolumForm.not || null,
            };
            if (bolumEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bolumEdit.bolumID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler`, payload);
            }
            setBolumModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deleteBolum = async (bId: number) => {
        if (!window.confirm('Bölümü ve paylaşımlarını silmek istediğinizden emin misiniz?')) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bId}`);
        fetchArsa();
    };

    // ─── Paylaşım ─────────────────────────────────────────────────────────────

    const openPaylasimCreate = (bolumId: number) => {
        setPaylasimBolumId(bolumId);
        setPaylasimEdit(null);
        setPaylasimForm({ ortakID: '', muteahhitID: '', oranYuzde: '', aciklamaNot: '' });
        setModalError('');
        setPaylasimModal(true);
    };
    const openPaylasimEdit = (bolumId: number, p: Paylasim) => {
        setPaylasimBolumId(bolumId);
        setPaylasimEdit(p);
        setPaylasimForm({ ortakID: p.ortakID !== null ? String(p.ortakID) : '', muteahhitID: p.muteahhitID !== null ? String(p.muteahhitID) : '', oranYuzde: String(p.oranYuzde), aciklamaNot: p.aciklamaNot ?? '' });
        setModalError('');
        setPaylasimModal(true);
    };
    const savePaylasim = async () => {
        if (paylasimBolumId === null) return;
        const oran = parseFloat(paylasimForm.oranYuzde);
        if (isNaN(oran) || oran <= 0) {
            setModalError('Paylaşım oranı 0\'dan büyük olmalıdır.');
            return;
        }
        setSaving(true); setModalError('');
        try {
            const payload = {
                ortakID: paylasimForm.ortakID !== '' ? parseInt(paylasimForm.ortakID) : null,
                muteahhitID: paylasimForm.muteahhitID !== '' ? parseInt(paylasimForm.muteahhitID) : null,
                oranYuzde: parseFloat(paylasimForm.oranYuzde) || 0,
                aciklamaNot: paylasimForm.aciklamaNot || null,
            };
            if (paylasimEdit) {
                await axiosInstance.put(`/api/KatKarsiligArsa/${arsaId}/bolumler/${paylasimBolumId}/paylasimlar/${paylasimEdit.paylasimID}`, payload);
            } else {
                await axiosInstance.post(`/api/KatKarsiligArsa/${arsaId}/bolumler/${paylasimBolumId}/paylasimlar`, payload);
            }
            setPaylasimModal(false);
            fetchArsa();
        } catch (e: any) { setModalError(e?.response?.data || 'Hata'); }
        finally { setSaving(false); }
    };
    const deletePaylasim = async (bolumId: number, pId: number) => {
        if (!window.confirm('Paylaşımı silmek istediğinizden emin misiniz?')) return;
        await axiosInstance.delete(`/api/KatKarsiligArsa/${arsaId}/bolumler/${bolumId}/paylasimlar/${pId}`);
        fetchArsa();
    };

    const fetchOneri = async () => {
        setOneriLoading(true);
        try {
            const res = await axiosInstance.get(`/api/KatKarsiligArsa/${arsaId}/paylasim-onerileri`);
            setOneriData(res.data);
            setOneriModal(true);
        } catch {
            alert('Öneri hesaplanamadı.');
        } finally {
            setOneriLoading(false);
        }
    };

    // ── Ortak rapor başlığı oluştur ──────────────────────────────────────────
    const pdfHeader = (doc: jsPDF, baslik: string) => {
        if (!arsa) return 36;
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 32;
        let y = 36;

        // Başlık bandı
        doc.setFillColor(26, 35, 64);
        doc.rect(0, 0, pageWidth, 52, 'F');
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(tr2pdf(baslik), margin, 32);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(new Date().toLocaleDateString('tr-TR'), pageWidth - margin, 32, { align: 'right' });
        doc.setTextColor(0);
        y = 64;

        // Arsa bilgi kutusu
        doc.setFillColor(245, 247, 252);
        doc.setDrawColor(200, 212, 235);
        doc.roundedRect(margin, y, pageWidth - margin * 2, 46, 4, 4, 'FD');

        const firmalar = arsa.muteahhitler.length > 0 ? arsa.muteahhitler.map(m => tr2pdf(m.adSoyad)).join(' / ') : '';
        const adaParsel = `Ada: ${arsa.ada ?? '-'}  Parsel: ${arsa.parsel ?? '-'}`;
        const toplamM2 = arsa.arsaMetrekare != null ? `Toplam: ${fmt(arsa.arsaMetrekare)} m2` : '';

        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        if (firmalar) doc.text(tr2pdf(`Firma: ${firmalar}`), margin + 10, y + 14);
        doc.setFont('helvetica', 'normal');
        doc.text(tr2pdf(adaParsel), margin + 10, y + 26);
        doc.text(tr2pdf(`Takip No: ${arsa.takipNo}`), margin + 10, y + 38);
        if (toplamM2) doc.text(toplamM2, pageWidth - margin - 10, y + 26, { align: 'right' });
        y += 56;

        // Turuncu ayırıcı çizgi
        doc.setDrawColor(230, 126, 34);
        doc.setLineWidth(1.5);
        doc.line(margin, y, pageWidth - margin, y);
        doc.setLineWidth(0.5);
        doc.setDrawColor(0);
        y += 12;
        return y;
    };

    const pdfFooter = (doc: jsPDF) => {
        const pageCount = (doc.internal as any).getNumberOfPages ? (doc.internal as any).getNumberOfPages() : 1;
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFillColor(26, 35, 64);
            doc.rect(0, pageHeight - 24, pageWidth, 24, 'F');
            doc.setFontSize(7.5);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(200, 210, 230);
            doc.text('Yapi CRM Sistemi Kullanilarak olusturulmustur', 32, pageHeight - 9);
            doc.text(`Sayfa ${i} / ${pageCount}`, pageWidth - 32, pageHeight - 9, { align: 'right' });
            doc.setTextColor(0);
        }
    };

    const exportOrtaklarPdf = () => {
        if (!arsa) return;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        const margin = 32;
        const y = pdfHeader(doc, 'Arsa Ortaklari Raporu');

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Ad Soyad', 'TC Kimlik', 'Telefon', 'Hisse m\u00b2', 'Hisse (%)', 'Pay (TL)', 'Aldigi Pay (TL)', 'Not']],
            body: arsa.ortaklar.map(o => [
                tr2pdf(o.adSoyad),
                o.tcKimlik ?? '-',
                o.telefon ?? '-',
                o.hisseMetre != null ? `${o.hisseMetre.toLocaleString('tr-TR')} m2` : '-',
                `%${o.hisseOrani.toLocaleString('tr-TR')}`,
                o.payTL != null ? `${fmt(o.payTL)} TL` : '-',
                o.aldigiPayTL != null ? `${fmt(o.aldigiPayTL)} TL` : '-',
                tr2pdf(o.not),
            ]),
            headStyles: { fillColor: [26, 35, 64], textColor: 255, fontStyle: 'bold', fontSize: 8 },
            alternateRowStyles: { fillColor: [245, 247, 252] },
            bodyStyles: { fontSize: 8 },
            didDrawPage: () => { /* footers drawn after */ },
        });

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`arsa-ortaklari${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const exportGayrimenkullerPdf = () => {
        if (!arsa) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const margin = 32;
        const y = pdfHeader(doc, 'Gayrimenkuller Raporu');

        autoTable(doc, {
            startY: y,
            margin: { left: margin, right: margin },
            head: [['Bolum Adi', 'Tip', 'Turu', 'Blok', 'Kat', 'Cepheler', 'm\u00b2', 'Fiyat (TL)', 'Birim Fiyat (TL)']],
            body: arsa.bolumler.map(b => {
                const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                    .filter(Boolean).map(c => tr2pdf(c!.cepheAdi)).join(', ') || '-';
                return [
                    tr2pdf(b.bolumAdi),
                    tr2pdf(b.bolumTipi),
                    tr2pdf(b.turu),
                    b.blok ? tr2pdf(b.blok.blokAdi) : '-',
                    b.kat ? tr2pdf(b.kat.katAdi) : '-',
                    cepheler,
                    `${b.m2.toLocaleString('tr-TR')} m2`,
                    `${fmt(b.fiyat)} TL`,
                    `${fmt(b.birimFiyat)} TL`,
                ];
            }),
            headStyles: { fillColor: [26, 35, 64], textColor: 255, fontStyle: 'bold', fontSize: 8 },
            alternateRowStyles: { fillColor: [245, 247, 252] },
            bodyStyles: { fontSize: 8 },
        });

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`gayrimenkuller${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const exportPaylasimlarPdf = () => {
        if (!arsa) return;
        const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 32;
        let y = pdfHeader(doc, 'Bolum Paylasim Listesi');

        arsa.bolumler.forEach((b, idx) => {
            // Sayfaya sığmıyorsa yeni sayfa
            if (y > pageHeight - 80) { doc.addPage(); y = 44; }

            const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                .filter(Boolean).map(c => tr2pdf(c!.cepheAdi)).join(', ') || '-';
            const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
            const tamamlandi = toplamOran >= 100;

            // Bölüm başlık bandı — başlık + meta ikinci satır
            const bandColor: [number, number, number] = tamamlandi ? [39, 174, 96] : [26, 35, 64];
            const metaParts: string[] = [];
            if (b.blok) metaParts.push(`Blok: ${tr2pdf(b.blok.blokAdi)}`);
            if (b.kat)  metaParts.push(`Kat: ${tr2pdf(b.kat.katAdi)}`);
            if (cepheler !== '-') metaParts.push(`Cephe: ${cepheler}`);
            metaParts.push(`${b.m2.toLocaleString('tr-TR')} m2`);
            metaParts.push(`${fmt(b.fiyat)} TL`);
            const bandH = metaParts.length > 0 ? 44 : 28;

            doc.setFillColor(...bandColor);
            doc.roundedRect(margin, y, pageWidth - margin * 2, bandH, 3, 3, 'F');

            // Başlık satırı
            doc.setFontSize(9.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            const bolumBaslik = tr2pdf(`${idx + 1}. ${b.bolumAdi}${b.bolumTipi ? ' - ' + b.bolumTipi : ''}`);
            doc.text(bolumBaslik, margin + 8, y + 17);
            const durumText = tamamlandi ? 'Tamamlandi' : `%${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} Dagitildi`;
            doc.setFontSize(8);
            doc.text(durumText, pageWidth - margin - 8, y + 17, { align: 'right' });

            // Meta satırı (band içinde, hafif saydam)
            if (metaParts.length > 0) {
                doc.setFontSize(7.5);
                doc.setFont('helvetica', 'normal');
                doc.setTextColor(tamamlandi ? 210 : 180, tamamlandi ? 240 : 200, tamamlandi ? 225 : 235);
                doc.text(metaParts.join('   |   '), margin + 8, y + 33);
            }

            doc.setTextColor(0);
            y += bandH + 6;

            // Paylaşım tablosu
            if (b.paylasimlar.length > 0) {
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin + 8, right: margin },
                    head: [['Kisi / Kurum', 'Tur', 'Oran (%)', 'Pay (TL)', 'Not']],
                    body: b.paylasimlar.map(p => [
                        tr2pdf(p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '-'),
                        p.ortak ? 'Arsa Ortagi' : p.muteahhit ? 'Muteahhit' : '-',
                        `%${p.oranYuzde.toLocaleString('tr-TR')}`,
                        `${fmt(b.fiyat * p.oranYuzde / 100)} TL`,
                        tr2pdf(p.aciklamaNot),
                    ]),
                    headStyles: { fillColor: [230, 126, 34], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
                    alternateRowStyles: { fillColor: tamamlandi ? [242, 250, 245] : [245, 247, 252] },
                    bodyStyles: { fontSize: 7.8 },
                    foot: [['', 'TOPLAM', `%${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`, `${fmt(b.fiyat * toplamOran / 100)} TL`, '']],
                    footStyles: { fillColor: [240, 242, 248], textColor: [26, 35, 64], fontStyle: 'bold', fontSize: 7.8 },
                });
                y = (doc as any).lastAutoTable.finalY + 6;
            } else {
                doc.setFontSize(7.8);
                doc.setFont('helvetica', 'italic');
                doc.setTextColor(160);
                doc.text('Henuz paylasim yapilmamis.', margin + 8, y + 8);
                doc.setTextColor(0);
                doc.setFont('helvetica', 'normal');
                y += 16;
            }

            y += 10;
        });

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`bolum-paylasimlar${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    const exportBlokGorunumPdf = (blokId: number) => {
        if (!arsa) return;
        const blok = arsa.bloklar.find(b => b.blokID === blokId);
        if (!blok) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageWidth  = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 28;
        let y = pdfHeader(doc, tr2pdf(`Blok Gorunum Raporu - ${blok.blokAdi}`));

        const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);
        if (katlar.length === 0) {
            doc.setFontSize(9); doc.setTextColor(150);
            doc.text('Bu blokta kat tanimli degil.', margin, y + 14);
            pdfFooter(doc);
            doc.save(`blok-gorunum_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
            return;
        }

        // Sütun genişlikleri: kat etiketi sabit, geri kalanı bölümlere bölünür
        const katLblW = 70;
        const contentW = pageWidth - margin * 2 - katLblW;
        const rowH = 36;  // her kat satırının yüksekliği
        const cellGap = 5;

        katlar.forEach(kat => {
            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);

            // Sayfa taşma kontrolü
            if (y + rowH + 4 > pageHeight - 30) { doc.addPage(); y = 36; }

            // Kat etiketi (lacivert)
            doc.setFillColor(26, 35, 64);
            doc.roundedRect(margin, y, katLblW, rowH, 2, 2, 'F');
            doc.setFontSize(8.5);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(tr2pdf(kat.katAdi), margin + katLblW / 2, y + rowH / 2 + 3, { align: 'center' });
            doc.setTextColor(0);

            if (bolumlerInKat.length === 0) {
                // Boş kat
                doc.setFillColor(248, 249, 252);
                doc.setDrawColor(220, 226, 240);
                doc.rect(margin + katLblW + cellGap, y, contentW - cellGap, rowH, 'FD');
                doc.setFontSize(7.5); doc.setTextColor(180);
                doc.text('–', margin + katLblW + cellGap + 10, y + rowH / 2 + 2.5);
                doc.setTextColor(0);
            } else {
                // Bölüm hücreleri — eşit genişlikte yan yana
                const cellCount = bolumlerInKat.length;
                const cellW = (contentW - cellGap - (cellCount - 1) * 3) / cellCount;
                bolumlerInKat.forEach((b, ci) => {
                    const cx = margin + katLblW + cellGap + ci * (cellW + 3);
                    // Hücre çerçevesi
                    doc.setFillColor(252, 253, 255);
                    doc.setDrawColor(200, 210, 230);
                    doc.roundedRect(cx, y, cellW, rowH, 2, 2, 'FD');
                    // Bölüm adı
                    doc.setFontSize(7.5);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(26, 35, 64);
                    const name = tr2pdf(b.bolumAdi);
                    doc.text(name, cx + cellW / 2, y + 11, { align: 'center', maxWidth: cellW - 4 });
                    // Tip
                    if (b.bolumTipi) {
                        doc.setFontSize(6.5);
                        doc.setFont('helvetica', 'normal');
                        doc.setTextColor(120);
                        doc.text(tr2pdf(b.bolumTipi), cx + cellW / 2, y + 20, { align: 'center', maxWidth: cellW - 4 });
                    }
                    // m2
                    doc.setFontSize(6.8);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(80);
                    doc.text(`${b.m2.toLocaleString('tr-TR')} m2`, cx + cellW / 2, y + rowH - 7, { align: 'center', maxWidth: cellW - 4 });
                    doc.setTextColor(0);
                });
            }

            y += rowH + 4;
        });

        pdfFooter(doc);
        doc.save(`blok-gorunum_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
    };

    const exportBlokGorunumPayliPdf = (blokId: number) => {
        if (!arsa) return;
        const blok = arsa.bloklar.find(b => b.blokID === blokId);
        if (!blok) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageWidth  = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin  = 28;
        let y = pdfHeader(doc, tr2pdf(`Blok Paylasim Raporu - ${blok.blokAdi}`));

        const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);
        if (katlar.length === 0) {
            doc.setFontSize(9); doc.setTextColor(150);
            doc.text('Bu blokta kat tanimli degil.', margin, y + 14);
            pdfFooter(doc);
            doc.save(`blok-paylasim_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
            return;
        }

        const katLblW = 70;
        const contentW = pageWidth - margin * 2 - katLblW;
        const cellGapX = 4;
        const rowGapY  = 5;
        const topPad = 7, bottomPad = 6;
        const lineH  = 9;

        // Computes required height (in pt) for a single cell
        const getCellHeight = (b: Bolum): number => {
            const hasCephe = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].some(c => c !== null);
            const payCount = b.paylasimlar.length;
            const hasPay   = payCount > 0;
            let h = topPad + bottomPad;
            h += lineH;             // bolümAdı
            h += lineH;             // tip + m²
            if (hasCephe) h += lineH; // cepheler satırı
            if (hasPay)   h += 6;   // ayraç çizgisi boşluğu
            h += payCount * lineH;  // her paylaşım satırı
            if (hasPay)   h += lineH; // toplam satırı
            return h;
        };

        katlar.forEach(kat => {
            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);

            const maxCellH = bolumlerInKat.length > 0
                ? Math.max(...bolumlerInKat.map(getCellHeight))
                : topPad + lineH + bottomPad;
            const rowH = Math.max(38, maxCellH);

            // Sayfa taşma kontrolü
            if (y + rowH + rowGapY > pageHeight - 30) { doc.addPage(); y = 36; }

            // Kat etiketi (lacivert)
            doc.setFillColor(26, 35, 64);
            doc.roundedRect(margin, y, katLblW, rowH, 2, 2, 'F');
            doc.setFontSize(8.5); doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(tr2pdf(kat.katAdi), margin + katLblW / 2, y + rowH / 2 + 3, { align: 'center' });
            doc.setTextColor(0);

            if (bolumlerInKat.length === 0) {
                doc.setFillColor(248, 249, 252);
                doc.setDrawColor(220, 226, 240);
                doc.rect(margin + katLblW + cellGapX, y, contentW - cellGapX, rowH, 'FD');
                doc.setFontSize(7.5); doc.setTextColor(180);
                doc.text('\u2013', margin + katLblW + cellGapX + 10, y + rowH / 2 + 2.5);
                doc.setTextColor(0);
            } else {
                const cellCount = bolumlerInKat.length;
                const cellW = (contentW - cellGapX - (cellCount - 1) * 3) / cellCount;

                bolumlerInKat.forEach((b, ci) => {
                    const cx = margin + katLblW + cellGapX + ci * (cellW + 3);
                    const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                        .filter(c => c !== null)
                        .map(c => c!.cepheAdi);
                    const toplamOran = b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);
                    const tamamlandi = toplamOran >= 100;
                    const hasPay = b.paylasimlar.length > 0;

                    // Hücre arka planı
                    if (tamamlandi) {
                        doc.setFillColor(232, 249, 240);
                        doc.setDrawColor(39, 174, 96);
                    } else if (hasPay) {
                        doc.setFillColor(255, 251, 245);
                        doc.setDrawColor(230, 160, 60);
                    } else {
                        doc.setFillColor(252, 253, 255);
                        doc.setDrawColor(200, 210, 230);
                    }
                    doc.setLineWidth(tamamlandi ? 1.2 : 0.5);
                    doc.roundedRect(cx, y, cellW, rowH, 2, 2, 'FD');
                    doc.setLineWidth(0.4);

                    let ly = y + topPad;

                    // Bölüm adı
                    doc.setFontSize(8); doc.setFont('helvetica', 'bold');
                    doc.setTextColor(
                        tamamlandi ? 21 : 26,
                        tamamlandi ? 115 : 35,
                        tamamlandi ? 55  : 64
                    );
                    doc.text(tr2pdf(b.bolumAdi), cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                    ly += lineH;

                    // Tip + m²
                    doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100);
                    const tipM2 = [b.bolumTipi ? tr2pdf(b.bolumTipi) : null, `${b.m2.toLocaleString('tr-TR')} m2`]
                        .filter(Boolean).join('  |  ');
                    doc.text(tipM2, cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                    ly += lineH;

                    // Cepheler
                    if (cepheler.length > 0) {
                        doc.setFontSize(6.5);
                        doc.setTextColor(tamamlandi ? 39 : 100, tamamlandi ? 160 : 120, tamamlandi ? 90 : 155);
                        doc.text(tr2pdf(cepheler.join(', ')), cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                        ly += lineH;
                    }

                    // Paylaşımlar
                    if (hasPay) {
                        // Ayraç çizgisi
                        doc.setDrawColor(tamamlandi ? 39 : 200, tamamlandi ? 174 : 210, tamamlandi ? 96 : 230);
                        doc.line(cx + 5, ly + 3, cx + cellW - 5, ly + 3);
                        ly += 6;

                        b.paylasimlar.forEach(p => {
                            const kisi = tr2pdf(p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '?');
                            doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
                            doc.setTextColor(
                                tamamlandi ? 28  : 55,
                                tamamlandi ? 130 : 70,
                                tamamlandi ? 65  : 100
                            );
                            doc.text(`${kisi}  %${p.oranYuzde}`, cx + cellW / 2, ly + 7, { align: 'center', maxWidth: cellW - 4 });
                            ly += lineH;
                        });

                        // Toplam satırı
                        doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
                        doc.setTextColor(
                            tamamlandi ? 39  : 100,
                            tamamlandi ? 174 : 100,
                            tamamlandi ? 96  : 100
                        );
                        doc.text(
                            `Toplam: %${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`,
                            cx + cellW / 2, ly + 7,
                            { align: 'center', maxWidth: cellW - 4 }
                        );
                    }

                    doc.setTextColor(0);
                });
            }

            y += rowH + rowGapY;
        });

        pdfFooter(doc);
        doc.save(`blok-paylasim_${tr2pdf(blok.blokAdi)}-${new Date().toISOString().slice(0,10)}.pdf`);
    };

    const exportVaziyetPlanPdf = async (blokId: number) => {
        if (!arsa) return;
        const blok = arsa.bloklar.find(b => b.blokID === blokId);
        if (!blok) return;

        const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);

        type AnyShape = {
            id: number; tool: string;
            x1: number; y1: number; x2: number; y2: number;
            color: string; filled: boolean; lineWidth: number;
            text?: string; fontSize?: number; fontBold?: boolean; fontItalic?: boolean;
            imageData?: string;
        };

        // Tüm katların çizimlerini paralel çek
        const shapesMap = new Map<number, AnyShape[]>();
        await Promise.all(katlar.map(async kat => {
            try {
                const res = await axiosInstance.get<AnyShape[]>(`/api/KatCizim/${kat.katID}`);
                shapesMap.set(kat.katID, res.data ?? []);
            } catch { shapesMap.set(kat.katID, []); }
        }));

        // Pusula görselini base64 olarak yükle
        const loadImgBase64 = (src: string): Promise<string> => new Promise(resolve => {
            const img = new Image();
            img.onload = () => {
                const c = document.createElement('canvas');
                c.width = img.width; c.height = img.height;
                c.getContext('2d')!.drawImage(img, 0, 0);
                resolve(c.toDataURL('image/png'));
            };
            img.onerror = () => resolve('');
            img.crossOrigin = 'anonymous';
            img.src = src;
        });
        const compassBase64 = await loadImgBase64(yonlerImg);

        // �?ekilleri ve pin'leri doğrudan PDF koordinatlarına çizer.
        // �?ekiller [0,1] normalize koordinatlarda saklanır;
        // Pin'ler doğrudan yüzde bazlı → dx + xVal*dw, dy + yVal*dh
        const renderInPdf = (
            shapes: AnyShape[], bolumlerInKat: Bolum[],
            dx: number, dy: number, dw: number, dh: number
        ) => {
            // �?ekil koordinatları [0,1] normalize — doğrudan dw/dh ile çarp
            // lineWidth/fontSize için referans ölçek (stil amaçlı)
            const REF = 800;
            const sx = dw / REF;
            const sy = dh / REF;

            // Beyaz zemin
            doc.setFillColor(255, 255, 255);
            doc.rect(dx, dy, dw, dh, 'F');

            // Izgara
            doc.setDrawColor(232, 237, 244); doc.setLineWidth(0.25);
            const GSX = 24 * sx;
            const GSY = 24 * sy;
            for (let gx = dx; gx <= dx + dw + 0.5; gx += GSX) doc.line(gx, dy, gx, dy + dh);
            for (let gy = dy; gy <= dy + dh + 0.5; gy += GSY) doc.line(dx, gy, dx + dw, gy);

            // �?ekiller
            shapes.forEach(s => {
                if (s.tool === 'image') {
                    // Resim şekillerini PNG olarak ekle
                    if (s.imageData) {
                        try {
                            doc.addImage(s.imageData, 'PNG',
                                dx + Math.min(s.x1, s.x2) * dw,
                                dy + Math.min(s.y1, s.y2) * dh,
                                Math.abs(s.x2 - s.x1) * dw,
                                Math.abs(s.y2 - s.y1) * dh
                            );
                        } catch { /* ignore */ }
                    }
                    return;
                }
                const x1 = dx + s.x1 * dw;
                const y1 = dy + s.y1 * dh;
                const x2 = dx + s.x2 * dw;
                const y2 = dy + s.y2 * dh;
                const w  = Math.abs(x2 - x1);
                const h  = Math.abs(y2 - y1);
                const lx = Math.min(x1, x2);
                const ly = Math.min(y1, y2);
                const lw = Math.max(s.lineWidth * Math.min(sx, sy) * 0.5, 0.4);

                doc.setDrawColor(s.color);
                doc.setFillColor(s.color);
                doc.setLineWidth(lw);

                if (s.tool === 'rect') {
                    doc.rect(lx, ly, w, h, s.filled ? 'F' : 'S');
                } else if (s.tool === 'oval') {
                    doc.ellipse(lx + w / 2, ly + h / 2, w / 2, h / 2, s.filled ? 'F' : 'S');
                } else if (s.tool === 'line') {
                    doc.line(x1, y1, x2, y2);
                } else if (s.tool === 'text' && s.text) {
                    const fs = Math.max((s.fontSize ?? 14) * Math.min(sx, sy), 5);
                    doc.setFontSize(fs);
                    doc.setFont('helvetica', s.fontBold ? 'bold' : (s.fontItalic ? 'italic' : 'normal'));
                    doc.text(tr2pdf(s.text), x1, y1);
                }
            });

            doc.setLineWidth(0.4);
            doc.setFont('helvetica', 'normal');

            // Cephe etiketleri (yön bazlı)
            arsa.cepheler.forEach(c => {
                const yon = (c.yon ?? '').trim().toLowerCase();
                if (!yon) return;
                const lbl = tr2pdf(c.cepheAdi);
                doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
                const approxW = lbl.length * 4 + 10;
                const lblH    = 11;
                let bx = 0, by = 0, valid = true;
                if      (yon === 'kuzey' || yon === 'k')                             { bx = dx + dw / 2 - approxW / 2; by = dy + 2; }
                else if (yon === 'güney' || yon === 'g' || yon === 'guney')          { bx = dx + dw / 2 - approxW / 2; by = dy + dh - lblH - 2; }
                else if (yon === 'batı'  || yon === 'b' || yon === 'bati')           { bx = dx + 2;              by = dy + dh / 2 - lblH / 2; }
                else if (yon === 'doğu'  || yon === 'd' || yon === 'dogu')           { bx = dx + dw - approxW - 2; by = dy + dh / 2 - lblH / 2; }
                else { valid = false; }
                if (!valid) return;
                doc.setFillColor(230, 126, 34); doc.setDrawColor(180, 80, 20); doc.setLineWidth(0.5);
                doc.roundedRect(bx, by, approxW, lblH, 2, 2, 'F');
                doc.setTextColor(255, 255, 255);
                doc.text(lbl, bx + 5, by + lblH - 3);
                doc.setTextColor(0);
            });

            // Bölüm pin'leri — xVal/yVal doğrudan drawing alanına uygulanır
            bolumlerInKat.forEach(b => {
                const px = dx + (b.xVal ?? 0.1) * dw;
                const py = dy + (b.yVal ?? 0.1) * dh;
                const toplamOran = b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);
                const tamamlandi = toplamOran >= 100;
                const pinW = 34; const pinH = 13;
                if (tamamlandi) { doc.setFillColor(39, 174, 96); doc.setDrawColor(21, 115, 55); }
                else             { doc.setFillColor(230, 126, 34); doc.setDrawColor(180, 80, 20); }
                doc.setLineWidth(0.7);
                doc.roundedRect(px - pinW / 2, py - pinH / 2, pinW, pinH, 2, 2, 'FD');
                doc.setFontSize(6.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255);
                const lbl = b.bolumAdi.length > 5 ? b.bolumAdi.substring(0, 5) : b.bolumAdi;
                doc.text(tr2pdf(lbl), px, py + 2, { align: 'center' });
                doc.setTextColor(0);
            });

            // Pusula — sağ üst
            if (compassBase64) {
                const CS = Math.min(38, dw * 0.08);
                doc.addImage(compassBase64, 'PNG', dx + dw - CS - 5, dy + 5, CS, CS);
            }

            // Dış kenarlık
            doc.setDrawColor(180, 190, 220); doc.setLineWidth(0.8);
            doc.rect(dx, dy, dw, dh);
            doc.setLineWidth(0.4);
        };

        // ── PDF oluştur ───────────────────────────────────────────────────
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageW  = doc.internal.pageSize.getWidth();
        const pageH  = doc.internal.pageSize.getHeight();
        const margin = 24;

        for (let ki = 0; ki < katlar.length; ki++) {
            const kat = katlar[ki];
            if (ki > 0) doc.addPage();

            let y = pdfHeader(doc, tr2pdf(`${blok.blokAdi} - Vaziyet Planlari`));

            // Kat etiketi bandı
            doc.setFillColor(230, 126, 34);
            doc.roundedRect(margin, y, 200, 22, 2, 2, 'F');
            doc.setFontSize(10); doc.setFont('helvetica', 'bold');
            doc.setTextColor(255, 255, 255);
            doc.text(tr2pdf(kat.katAdi), margin + 10, y + 15);
            doc.setTextColor(0);
            y += 30;

            const usableH  = pageH - y - margin - 26;
            const contentW = pageW - margin * 2;

            // En/boy oranı — KatCizimPanel ile aynı öncelik sırası
            const ratioVal = kat.en && kat.boy && kat.boy > 0
                ? kat.en / kat.boy
                : blok.en && blok.boy && blok.boy > 0
                    ? blok.en / blok.boy
                    : 4 / 3;

            // Çizim alanı: sayfanın yarısı genişlik, oran korunur
            // Yükseklik aşılırsa KatCizimPanel gibi GENİ�?Lİ�?İ küçült
            let drawingW = Math.floor(pageW / 2);
            let drawingH = Math.round(drawingW / ratioVal);
            if (drawingH > usableH) {
                drawingH = Math.floor(usableH);
                drawingW = Math.round(drawingH * ratioVal);
            }

            const infoW = pageW - margin - (margin + drawingW + 10) - 2;
            const infoX = margin + drawingW + 10;
            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);
            const shapes = shapesMap.get(kat.katID) ?? [];

            renderInPdf(shapes, bolumlerInKat, margin, y, drawingW, drawingH);

            // ── Sağ panel: bölüm bilgi kartları ─────────────────────────
            const lineH  = 9;
            const cardPad = 5;

            if (bolumlerInKat.length === 0) {
                doc.setFontSize(7.5); doc.setTextColor(160);
                doc.text('Bu katta bolum tanimli degil.', infoX + cardPad, y + 14);
                doc.setTextColor(0);
            }

            let iy = y;
            bolumlerInKat.forEach(b => {
                const toplamOran = b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0);
                const tamamlandi = toplamOran >= 100;
                const hasPay     = b.paylasimlar.length > 0;
                const cepheler   = [b.cephe1, b.cephe2, b.cephe3, b.cephe4]
                    .filter(c => c !== null).map(c => c!.cepheAdi);

                let cardH = cardPad + lineH + lineH
                           + (cepheler.length > 0 ? lineH : 0)
                           + (hasPay ? 5 + b.paylasimlar.length * lineH + lineH : 0)
                           + cardPad;

                if (iy + cardH > y + drawingH) return;

                if (tamamlandi) { doc.setFillColor(232, 249, 240); doc.setDrawColor(39, 174, 96); }
                else if (hasPay){ doc.setFillColor(255, 251, 245); doc.setDrawColor(230, 160, 60); }
                else             { doc.setFillColor(252, 253, 255); doc.setDrawColor(200, 210, 230); }
                doc.setLineWidth(tamamlandi ? 1 : 0.4);
                doc.roundedRect(infoX, iy, infoW, cardH, 2, 2, 'FD');
                doc.setLineWidth(0.4);

                let ly = iy + cardPad;

                doc.setFontSize(8); doc.setFont('helvetica', 'bold');
                doc.setTextColor(tamamlandi ? 21 : 26, tamamlandi ? 115 : 35, tamamlandi ? 55 : 64);
                doc.text(tr2pdf(b.bolumAdi), infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                ly += lineH;

                doc.setFontSize(6.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(100);
                const tipM2 = [b.bolumTipi ? tr2pdf(b.bolumTipi) : null, `${b.m2.toLocaleString('tr-TR')} m2`]
                    .filter(Boolean).join('  |  ');
                doc.text(tipM2, infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                ly += lineH;

                if (cepheler.length > 0) {
                    doc.setFontSize(6.5); doc.setTextColor(80, 110, 160);
                    doc.text(tr2pdf(cepheler.join(', ')), infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                    ly += lineH;
                }

                if (hasPay) {
                    doc.setDrawColor(tamamlandi ? 39 : 200, tamamlandi ? 174 : 210, tamamlandi ? 96 : 230);
                    doc.line(infoX + 4, ly + 2, infoX + infoW - 4, ly + 2);
                    ly += 5;
                    b.paylasimlar.forEach(p => {
                        const kisi = tr2pdf(p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '?');
                        doc.setFontSize(6.5); doc.setFont('helvetica', 'normal');
                        doc.setTextColor(tamamlandi ? 28 : 55, tamamlandi ? 130 : 70, tamamlandi ? 65 : 100);
                        doc.text(`${kisi}  %${p.oranYuzde}`, infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 });
                        ly += lineH;
                    });
                    doc.setFontSize(6.5); doc.setFont('helvetica', 'bold');
                    doc.setTextColor(tamamlandi ? 39 : 100, tamamlandi ? 174 : 100, tamamlandi ? 96 : 100);
                    doc.text(
                        `Toplam: %${toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}`,
                        infoX + cardPad, ly + 7, { maxWidth: infoW - cardPad * 2 }
                    );
                }

                doc.setTextColor(0);
                iy += cardH + 4;
            });
        }

        pdfFooter(doc);
        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`vaziyet-plan_${tr2pdf(blok.blokAdi)}${takip}-${new Date().toISOString().slice(0,10)}.pdf`);
    };

    // jsPDF helvetica fontu Turkce karakter desteklemiyor; ASCII karsiligi kullanilir
    const tr2pdf = (s: string | null | undefined): string => (s ?? '')
        .replace(/\u011f/g, 'g').replace(/\u011e/g, 'G')
        .replace(/\u00fc/g, 'u').replace(/\u00dc/g, 'U')
        .replace(/\u015f/g, 's').replace(/\u015e/g, 'S')
        .replace(/\u0131/g, 'i').replace(/\u0130/g, 'I')
        .replace(/\u00f6/g, 'o').replace(/\u00d6/g, 'O')
        .replace(/\u00e7/g, 'c').replace(/\u00c7/g, 'C');

    const exportOneriPdf = () => {
        if (!oneriData || !arsa) return;
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 32;
        let y = 36;

        // ── Ana başlık ──────────────────────────────────────────────────────
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        doc.text('Akilli Dagitim Onerileri', margin, y);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(`Tarih: ${new Date().toLocaleDateString('tr-TR')}`, pageWidth - margin, y, { align: 'right' });
        y += 18;

        // ── Arsa bilgi kutusu ────────────────────────────────────────────────
        // Muteahhitler adlarını firma olarak kullan
        const firmalar = arsa.muteahhitler.map(m => tr2pdf(m.adSoyad)).join(' / ');
        const lokasyon = [arsa.mahalle, arsa.ilce, arsa.il].filter(Boolean).map(tr2pdf).join(' / ');
        const adaParsel = `Ada: ${arsa.ada ?? '-'}  Parsel: ${arsa.parsel ?? '-'}`;
        const m2Bilgi = arsa.arsaMetrekare != null ? `Toplam Alan: ${fmt(arsa.arsaMetrekare)} m2` : '';
        const degerBilgi = arsa.arsaTutar != null ? `Toplam Deger: ${fmt(arsa.arsaTutar)} TL` : '';
        const toplamKalan = `Toplam Kalan Dagitilacak: ${fmt(oneriData.toplamKalanTL)} TL`;

        doc.setFillColor(240, 244, 255);
        doc.setDrawColor(180, 190, 220);
        doc.roundedRect(margin, y, pageWidth - margin * 2, 52, 4, 4, 'FD');

        doc.setFontSize(9);
        doc.setFont('helvetica', 'bold');
        if (firmalar) doc.text(`Firma: ${firmalar}`, margin + 10, y + 14);
        doc.setFont('helvetica', 'normal');
        doc.text(adaParsel, margin + 10, y + 26);
        if (lokasyon) doc.text(lokasyon, margin + 10, y + 38);
        const sag = pageWidth - margin - 10;
        if (m2Bilgi) doc.text(m2Bilgi, sag, y + 14, { align: 'right' });
        if (degerBilgi) doc.text(degerBilgi, sag, y + 26, { align: 'right' });
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(30, 80, 180);
        doc.text(toplamKalan, sag, y + 38, { align: 'right' });
        doc.setTextColor(0);
        doc.setFont('helvetica', 'normal');

        y += 62;
        doc.setDrawColor(180);
        doc.line(margin, y, pageWidth - margin, y);
        y += 14;

        // ── Bölümler ────────────────────────────────────────────────────────
        oneriData.bolumler.forEach(b => {
            if (b.kalanOran <= 0 && b.oneriler.length === 0) return;

            if (y > doc.internal.pageSize.getHeight() - 70) { doc.addPage(); y = 36; }

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(tr2pdf(`${b.bolumAdi}${b.bolumTipi ? ' - ' + b.bolumTipi : ''}`), margin, y);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            const bolumBilgi = `Fiyat: ${fmt(b.fiyat)} TL   Dagitilan: %${b.dagitilanOran.toFixed(2)}   Kalan: %${b.kalanOran.toFixed(2)}${b.atanmayanFiyat > 0 ? '   Atanamayan: ' + fmt(b.atanmayanFiyat) + ' TL' : ''}`;
            doc.text(bolumBilgi, margin, y + 12);
            y += 26;

            // Mevcut paylaşımlar
            if (b.mevcutPaylasimlar.length > 0) {
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Ad Soyad', 'Tur', 'Oran (%)', 'Pay (TL)']],
                    body: b.mevcutPaylasimlar.map(p => [
                        tr2pdf(p.adSoyad),
                        p.tip === 'Ortak' ? 'Arsa Ortagi' : 'Muteahhit',
                        `%${p.oranYuzde}`,
                        `${fmt(p.payTL)} TL`
                    ]),
                    headStyles: { fillColor: [200, 210, 230], textColor: 30, fontStyle: 'bold', fontSize: 7.5 },
                    bodyStyles: { fontSize: 7.5 },
                    tableWidth: 'auto'
                });
                y = (doc as any).lastAutoTable.finalY + 8;
            }

            // Öneriler
            if (b.oneriler.length > 0) {
                autoTable(doc, {
                    startY: y,
                    margin: { left: margin, right: margin },
                    head: [['Ad Soyad', 'Tur', 'Toplam Hak (TL)', 'Onceki Kalan (TL)', 'Oneri Oran', 'Oneri TL', 'Sonraki Kalan (TL)', 'Durum']],
                    body: b.oneriler.map(o => [
                        tr2pdf(o.adSoyad),
                        o.tip === 'Ortak' ? 'Arsa Ortagi' : 'Muteahhit',
                        `${fmt(o.payTL)} TL`,
                        `${fmt(o.kalanOncesi)} TL`,
                        `%${o.oneriOran.toFixed(2)}`,
                        `${fmt(o.oneriTL)} TL`,
                        `${fmt(o.kalanSonra)} TL`,
                        o.durum === 'tamamlandi' ? 'Tamamlandi' : `${fmt(o.kalanSonra)} TL devam`
                    ]),
                    headStyles: { fillColor: [26, 35, 64], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
                    bodyStyles: { fontSize: 7.5 },
                    columnStyles: { 4: { fontStyle: 'bold' }, 5: { fontStyle: 'bold' } }
                });
                y = (doc as any).lastAutoTable.finalY + 16;
            }
        });

        // Atanamayanlar
        if (oneriData.atanamamis && oneriData.atanamamis.length > 0) {
            if (y > doc.internal.pageSize.getHeight() - 80) { doc.addPage(); y = 36; }
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Dagitilamamis Kisiler (${oneriData.atanamamis.length})`, margin, y);
            y += 14;
            autoTable(doc, {
                startY: y,
                margin: { left: margin, right: margin },
                head: [['Ad Soyad', 'Tur', 'Toplam Hak (TL)', 'Dagitilamamis (TL)']],
                body: oneriData.atanamamis.map(a => [
                    tr2pdf(a.adSoyad),
                    a.tip === 'Ortak' ? 'Arsa Ortagi' : 'Muteahhit',
                    `${fmt(a.payTL)} TL`,
                    `${fmt(a.kalanTL)} TL`
                ]),
                headStyles: { fillColor: [192, 57, 43], textColor: 255, fontStyle: 'bold', fontSize: 7.5 },
                bodyStyles: { fontSize: 7.5 }
            });
        }

        const takip = arsa.takipNo ? `_${arsa.takipNo}` : '';
        doc.save(`dagitim-onerileri${takip}-${new Date().toISOString().slice(0, 10)}.pdf`);
    };

    // ─── Yardımcılar ──────────────────────────────────────────────────────────

    // Paylaşılan alan: paylasimlar oranlarından hesapla
    const bolumPaylasılanAlan = (b: Bolum): number => {
        const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
        return b.m2 * Math.min(toplamOran, 100) / 100;
    };

    // Seçilen bloğa ait katlar
    const katsForBlok = (blokId: string) => {
        if (!arsa || !blokId) return [];
        const blok = arsa.bloklar.find(b => b.blokID === parseInt(blokId));
        return blok?.katlar ?? [];
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    if (loading) return <div className="kka-detay-page"><div className="kka-loading">Yükleniyor...</div></div>;
    if (!arsa) return <div className="kka-detay-page"><div className="kka-loading">Arsa bulunamadı.</div></div>;

    const tabs: { key: TabKey; label: string }[] = [
        { key: 'bolumler', label: `Bölümler (${arsa.bolumler.length})` },
        { key: 'genel', label: 'Genel Bilgiler' },
        { key: 'muteahhitler', label: `Müteahhitler (${arsa.muteahhitler.length})` },
        { key: 'ortaklar', label: `Arsa Ortakları (${arsa.ortaklar.length})` },
        { key: 'bloklar', label: `Bloklar & Katlar (${arsa.bloklar.length})` },
        { key: 'cepheler', label: `Cepheler (${arsa.cepheler.length})` },
        { key: 'paylasimlar', label: 'Paylaşımlar' },
    ];

    return (
        <div className="kka-detay-page">
            {/* Sayfa Başlığı */}
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#0f172a', margin: 0, letterSpacing: '-0.02em' }}>
                        Kat Karşılığı Sözleşmesi
                        {(arsa.ada || arsa.parsel) && (
                            <span style={{ color: '#64748b', fontWeight: 500 }}>
                                {' – '}
                                {[arsa.ada ? `Ada: ${arsa.ada}` : null, arsa.parsel ? `Parsel: ${arsa.parsel}` : null].filter(Boolean).join(' | ')}
                            </span>
                        )}
                    </h1>
                    <p style={{ fontSize: '13.5px', color: '#64748b', margin: 0 }}>
                        {[arsa.takipNo, arsa.il, arsa.ilce, arsa.mahalle].filter(Boolean).join(' · ')}
                    </p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div className="kka-indir-wrap">
                        <button className="kka-indir-btn" style={{ background: '#1a2340', color: '#fff' }} onClick={() => setShowIslemler(v => !v)}>
                            <i className="fas fa-cog" style={{ marginRight: 6 }}></i>İşlemler
                            <i className="fas fa-caret-down" style={{ marginLeft: 6, fontSize: '0.75rem' }}></i>
                        </button>
                        {showIslemler && (
                            <div className="kka-indir-menu" onMouseLeave={() => setShowIslemler(false)}>
                                <button className="kka-indir-item" onClick={() => { setShowIslemler(false); setActiveTab('genel'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
                                    <i className="fas fa-info-circle" style={{ marginRight: 8, color: '#1a2340' }}></i>Detaylar
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="kka-indir-wrap">
                        <button className="kka-indir-btn" onClick={() => setShowIndir(v => !v)}>
                            <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>İndir
                            <i className="fas fa-caret-down" style={{ marginLeft: 6, fontSize: '0.75rem' }}></i>
                        </button>
                        {showIndir && (
                            <div className="kka-indir-menu" onMouseLeave={() => setShowIndir(false)}>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); exportOrtaklarPdf(); }}>
                                    <i className="fas fa-users" style={{ marginRight: 8, color: '#cd6906' }}></i>Arsa Ortakları Raporu
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); exportGayrimenkullerPdf(); }}>
                                    <i className="fas fa-building" style={{ marginRight: 8, color: '#cd6906' }}></i>Gayrimenkuller Raporu
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); exportPaylasimlarPdf(); }}>
                                    <i className="fas fa-list-alt" style={{ marginRight: 8, color: '#cd6906' }}></i>Bölüm Paylaşım Listesi
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); setBlokRaporBlokId(null); setShowBlokRaporModal(true); }}>
                                    <i className="fas fa-th-large" style={{ marginRight: 8, color: '#cd6906' }}></i>Blok Görünüm Raporu
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); setBlokRaporPayliBlokId(null); setShowBlokRaporPayliModal(true); }}>
                                    <i className="fas fa-th-large" style={{ marginRight: 8, color: '#27ae60' }}></i>Blok Görünüm Raporu (Paylaşımlı)
                                </button>
                                <button className="kka-indir-item" onClick={() => { setShowIndir(false); setVaziyetBlokId(null); setShowVaziyetModal(true); }}>
                                    <i className="fas fa-map-marked-alt" style={{ marginRight: 8, color: '#8e44ad' }}></i>Vaziyet Planları
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Özet Kartları */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 0, flexWrap: 'wrap' }}>
                {/* Arsa Alanı */}
                <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 160, cursor: 'default' }}>
                    <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="fas fa-ruler-combined" style={{ fontSize: 15, color: '#64748b' }} />
                    </span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.04em', flexShrink: 0 }}>
                        {arsa.arsaMetrekare !== null ? arsa.arsaMetrekare.toLocaleString('tr-TR') : '—'}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                        Arsa m²
                    </span>
                </div>

                {/* Bölüm Adedi */}
                <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 160, cursor: 'default' }}>
                    <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <i className="fas fa-th-large" style={{ fontSize: 15, color: '#64748b' }} />
                    </span>
                    <span style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.04em', flexShrink: 0 }}>
                        {arsa.bolumler.length}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                        Bölüm Adedi
                    </span>
                </div>

                {/* Toplam Değer */}
                <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 200, cursor: 'default' }}>
                    <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontSize: 18, fontWeight: 800, color: '#64748b' }}>₺</span>
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.03em', flexShrink: 0 }}>
                        {arsa.arsaTutar !== null
                            ? arsa.arsaTutar.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₺'
                            : '—'}
                    </span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                        Toplam Değer
                    </span>
                </div>

                {/* Paylaşılma Durumu */}
                {(() => {
                    const toplamBolum = arsa.bolumler.length;
                    const paylasimliSayi = arsa.bolumler.filter(b => b.paylasimlar.reduce((s, p) => s + p.oranYuzde, 0) >= 100).length;
                    const paylasimOrani = toplamBolum > 0 ? Math.round((paylasimliSayi / toplamBolum) * 100) : 0;
                    return (
                        <div style={{ background: '#f3f4f6', borderRadius: 8, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 180, cursor: 'default' }}>
                            <span style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <i className="fas fa-chart-pie" style={{ fontSize: 15, color: '#64748b' }} />
                            </span>
                            <div style={{ flexShrink: 0 }}>
                                <div style={{ fontSize: 22, fontWeight: 800, color: '#1e293b', lineHeight: 1, letterSpacing: '-0.04em' }}>%{paylasimOrani}</div>
                                <div style={{ background: '#e5e7eb', borderRadius: 4, height: 4, width: 60, overflow: 'hidden', marginTop: 5 }}>
                                    <div style={{ width: `${paylasimOrani}%`, background: '#cd6906', height: '100%', borderRadius: 4 }} />
                                </div>
                            </div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', letterSpacing: '0.3px', lineHeight: 1.3 }}>
                                Paylaşılma<br /><span style={{ color: '#9ca3af', fontWeight: 400 }}>{paylasimliSayi}/{toplamBolum} bölüm</span>
                            </span>
                        </div>
                    );
                })()}
            </div>

            {/* Tabs */}
            <div className="kka-tabs" style={{ marginTop: 32 }}>
                {tabs.map(t => (
                    <button
                        key={t.key}
                        className={`kka-tab-btn${activeTab === t.key ? ' active' : ''}`}
                        onClick={() => setActiveTab(t.key)}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* ── Genel Tab ── */}
            {activeTab === 'genel' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>Arsa Bilgileri</h4>
                    </div>
                    <div className="kka-info-grid">
                        <div className="kka-info-item"><label>Takip No</label><span>{arsa.takipNo}</span></div>
                        <div className="kka-info-item"><label>Ada</label><span>{arsa.ada ?? '–'}</span></div>
                        <div className="kka-info-item"><label>Parsel</label><span>{arsa.parsel ?? '–'}</span></div>
                        <div className="kka-info-item"><label>İl</label><span>{arsa.il ?? '–'}</span></div>
                        <div className="kka-info-item"><label>İlçe</label><span>{arsa.ilce ?? '–'}</span></div>
                        <div className="kka-info-item"><label>Mahalle</label><span>{arsa.mahalle ?? '–'}</span></div>
                        <div className="kka-info-item"><label>Alan (m²)</label><span>{arsa.arsaMetrekare !== null ? `${arsa.arsaMetrekare.toLocaleString('tr-TR')} m²` : '–'}</span></div>
                        <div className="kka-info-item"><label>Toplam Tutar</label><span>{arsa.arsaTutar !== null ? `${arsa.arsaTutar.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺` : '–'}</span></div>
                        <div className="kka-info-item"><label>Durum</label><span><span className={`kka-badge ${arsa.aktifMi ? 'aktif' : 'pasif'}`}>{arsa.aktifMi ? 'Aktif' : 'Pasif'}</span></span></div>
                    </div>
                    {arsa.aciklama && (
                        <div style={{ marginTop: 16, padding: '12px 16px', background: '#f8f9fb', borderRadius: 8, fontSize: '0.9rem', color: '#444' }}>
                            {arsa.aciklama}
                        </div>
                    )}
                </div>
            )}

            {/* ── Müteahhitler Tab ── */}
            {activeTab === 'muteahhitler' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>Müteahhitler</h4>
                        <button className="kka-btn-primary" onClick={openMuteahhitCreate}>+ Müteahhit Ekle</button>
                    </div>
                    {arsa.muteahhitler.length === 0 ? (
                        <div className="kka-empty">Henüz müteahhit eklenmemiş.</div>
                    ) : (
                        <table className="kka-sub-table">
                            <thead><tr><th>Ad Soyad</th><th>Telefon</th><th>E-Posta</th><th>Vergi No</th><th>Hisse m²</th><th>Pay (%)</th><th>Toplam Pay (%)</th><th>Pay (TL)</th><th>Aldığı Pay (TL)</th><th>Not</th><th></th></tr></thead>
                            <tbody>
                                {arsa.muteahhitler.map(m => {
                                    const mPays = arsa.bolumler
                                        .filter(b => b.paylasimlar.some(p => p.muteahhitID === m.muteahhitID))
                                        .map(b => ({ b, p: b.paylasimlar.find(p => p.muteahhitID === m.muteahhitID)! }));
                                    return (
                                    <tr key={m.muteahhitID}>
                                        <td>
                                            <strong>{m.adSoyad}</strong>
                                            {mPays.length > 0 && (
                                                <div className="kka-pay-list">
                                                    {mPays.map(({ b, p }) => {
                                                        const key = `m-${m.muteahhitID}-${b.bolumID}`;
                                                        const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].filter(Boolean).map(c => c!.cepheAdi).join(', ');
                                                        return (
                                                            <span
                                                                key={key}
                                                                className="kka-pay-chip"
                                                                onMouseEnter={() => setHoveredPayKey(key)}
                                                                onMouseLeave={() => setHoveredPayKey(null)}
                                                            >
                                                                {b.bolumAdi}: %{p.oranYuzde}
                                                                {hoveredPayKey === key && (
                                                                    <div className="kka-pay-tooltip">
                                                                        <div className="kka-pay-tooltip-title">{b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}</div>
                                                                        {b.blok && <div className="kka-pay-tooltip-row"><span>Blok</span><span>{b.blok.blokAdi}</span></div>}
                                                                        {b.kat && <div className="kka-pay-tooltip-row"><span>Kat</span><span>{b.kat.katAdi}</span></div>}
                                                                        {cepheler && <div className="kka-pay-tooltip-row"><span>Cephe</span><span>{cepheler}</span></div>}
                                                                        <div className="kka-pay-tooltip-row"><span>M²</span><span>{b.m2.toLocaleString('tr-TR')} m²</span></div>
                                                                        <div className="kka-pay-tooltip-row"><span>Fiyat</span><span>{fmt(b.fiyat)} ₺</span></div>
                                                                        <div className="kka-pay-tooltip-row kka-pay-tooltip-oran"><span>Oran</span><span>%{p.oranYuzde}</span></div>
                                                                        <div className="kka-pay-tooltip-row kka-pay-tooltip-tl"><span>Pay (TL)</span><span>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</span></div>
                                                                    </div>
                                                                )}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td>{m.telefon ?? '–'}</td>
                                        <td>{m.eposta ?? '–'}</td>
                                        <td>{m.vergiNo ?? '–'}</td>
                                        <td>{m.hisseMetre !== null ? `${m.hisseMetre.toLocaleString('tr-TR')} m²` : '–'}</td>
                                        <td>%{m.payYuzde.toLocaleString('tr-TR')}</td>
                                        <td>%{m.toplamPayYuzde.toLocaleString('tr-TR')}</td>
                                        <td>{fmt(m.payTL)} ₺</td>
                                        <td>{fmt(m.aldigiPayTL)} ₺</td>
                                        <td>{m.not ?? '–'}</td>
                                        <td>
                                            <div className="kka-td-actions">
                                                <button className="kka-icon-btn edit" title="Düzenle" onClick={() => openMuteahhitEdit(m)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                <button className="kka-icon-btn del" title="Sil" onClick={() => deleteMuteahhit(m.muteahhitID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ── Ortaklar Tab ── */}
            {activeTab === 'ortaklar' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>Arsa Ortakları</h4>
                        <button className="kka-btn-primary" onClick={openOrtakCreate}>+ Ortak Ekle</button>
                    </div>
                    {arsa.ortaklar.length === 0 ? (
                        <div className="kka-empty">Henüz ortak eklenmemiş.</div>
                    ) : (
                        <table className="kka-sub-table">
                            <thead><tr><th>Ad Soyad</th><th>TC Kimlik</th><th>Telefon</th><th>E-Posta</th><th>Hisse m²</th><th>Hisse (%)</th><th>Pay (TL)</th><th>Aldığı Pay (TL)</th><th>Not</th><th></th></tr></thead>
                            <tbody>
                                {arsa.ortaklar.map(o => {
                                    const oPays = arsa.bolumler
                                        .filter(b => b.paylasimlar.some(p => p.ortakID === o.ortakID))
                                        .map(b => ({ b, p: b.paylasimlar.find(p => p.ortakID === o.ortakID)! }));
                                    return (
                                    <tr key={o.ortakID}>
                                        <td>
                                            <strong>{o.adSoyad}</strong>
                                            {oPays.length > 0 && (
                                                <div className="kka-pay-list">
                                                    {oPays.map(({ b, p }) => {
                                                        const key = `o-${o.ortakID}-${b.bolumID}`;
                                                        const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].filter(Boolean).map(c => c!.cepheAdi).join(', ');
                                                        return (
                                                            <span
                                                                key={key}
                                                                className="kka-pay-chip"
                                                                onMouseEnter={() => setHoveredPayKey(key)}
                                                                onMouseLeave={() => setHoveredPayKey(null)}
                                                            >
                                                                {b.bolumAdi}: %{p.oranYuzde}
                                                                {hoveredPayKey === key && (
                                                                    <div className="kka-pay-tooltip">
                                                                        <div className="kka-pay-tooltip-title">{b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}</div>
                                                                        {b.blok && <div className="kka-pay-tooltip-row"><span>Blok</span><span>{b.blok.blokAdi}</span></div>}
                                                                        {b.kat && <div className="kka-pay-tooltip-row"><span>Kat</span><span>{b.kat.katAdi}</span></div>}
                                                                        {cepheler && <div className="kka-pay-tooltip-row"><span>Cephe</span><span>{cepheler}</span></div>}
                                                                        <div className="kka-pay-tooltip-row"><span>M²</span><span>{b.m2.toLocaleString('tr-TR')} m²</span></div>
                                                                        <div className="kka-pay-tooltip-row"><span>Fiyat</span><span>{fmt(b.fiyat)} ₺</span></div>
                                                                        <div className="kka-pay-tooltip-row kka-pay-tooltip-oran"><span>Oran</span><span>%{p.oranYuzde}</span></div>
                                                                        <div className="kka-pay-tooltip-row kka-pay-tooltip-tl"><span>Pay (TL)</span><span>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</span></div>
                                                                    </div>
                                                                )}
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </td>
                                        <td>{o.tcKimlik ?? '–'}</td>
                                        <td>{o.telefon ?? '–'}</td>
                                        <td>{o.eposta ?? '–'}</td>
                                        <td>{o.hisseMetre !== null ? `${o.hisseMetre.toLocaleString('tr-TR')} m²` : '–'}</td>
                                        <td>%{o.hisseOrani.toLocaleString('tr-TR')}</td>
                                        <td>{fmt(o.payTL)} ₺</td>
                                        <td>{fmt(o.aldigiPayTL)} ₺</td>
                                        <td>{o.not ?? '–'}</td>
                                        <td>
                                            <div className="kka-td-actions">
                                                <button className="kka-icon-btn edit" onClick={() => openOrtakEdit(o)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                <button className="kka-icon-btn del" onClick={() => deleteOrtak(o.ortakID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                            </div>
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ── Bloklar & Katlar Tab ── */}
            {activeTab === 'bloklar' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <h4 style={{ margin: 0 }}>Bloklar & Katlar</h4>
                            <select
                                className="kka-input"
                                style={{ minWidth: 180, padding: '5px 10px', fontSize: '0.9rem' }}
                                value={selectedBlokId ?? ''}
                                onChange={e => {
                                    setSelectedBlokId(e.target.value ? Number(e.target.value) : null);
                                    setInlineKatBlokId(null);
                                }}
                            >
                                <option value="">-- Blok Seçin --</option>
                                {arsa.bloklar.map(b => (
                                    <option key={b.blokID} value={b.blokID}>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button className="kka-btn-primary" onClick={openBlokCreate}>+ Blok Ekle</button>
                    </div>

                    {arsa.bloklar.length === 0 ? (
                        <div className="kka-empty">Henüz blok eklenmemiş.</div>
                    ) : !selectedBlokId ? (
                        <div className="kka-empty">Lütfen yukarıdan bir blok seçin.</div>
                    ) : (() => {
                        const b = arsa.bloklar.find(x => x.blokID === selectedBlokId);
                        if (!b) return null;
                        const katAdet = b.katlar.length;
                        const beklenen = b.katSayisi;
                        const uyari = beklenen !== null && katAdet !== beklenen
                            ? (katAdet < beklenen ? `${beklenen - katAdet} kat eksik` : `${katAdet - beklenen} kat fazla`)
                            : null;
                        return (
                            <div style={{ display: 'flex', gap: 0, alignItems: 'flex-start' }}>
                                {/* Sol 1/4: blok kartı */}
                                <div style={{ width: '25%', minWidth: 220 }}>
                                <div className="kka-blok-card" style={{ margin: 0 }}>
                                    <div className="kka-blok-card-header">
                                        <span>
                                            {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                            {b.en || b.boy ? <span style={{ fontSize: '0.75rem', color: '#aaa', marginLeft: 6 }}>{b.en ?? '?'}×{b.boy ?? '?'}</span> : null}
                                        </span>
                                        <div className="kka-blok-card-actions">
                                            {uyari && <span style={{ fontSize: '0.75rem', color: '#cd6906', fontWeight: 600, marginRight: 6 }}><i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }}></i>{uyari}</span>}
                                            <button className="kka-icon-btn edit" onClick={() => openBlokEdit(b)}><i className="fas fa-pen"></i></button>
                                            <button className="kka-icon-btn del" onClick={() => deleteBlok(b.blokID)}><i className="fas fa-trash"></i></button>
                                        </div>
                                    </div>
                                    <div className="kka-blok-card-body">
                                        {b.katlar.length === 0 ? (
                                            <p style={{ fontSize: '0.83rem', color: '#bbb', margin: '6px 0' }}>Henüz kat yok.</p>
                                        ) : (
                                            b.katlar.sort((a, c) => a.siraNo - c.siraNo).map(k => (
                                                <div className="kka-kat-row" key={k.katID} style={{ background: cizimKat?.kat.katID === k.katID ? '#eef3ff' : undefined }}>
                                                    <span>{k.katAdi}{(k.en || k.boy) ? <span style={{ fontSize: '0.72rem', color: '#bbb', marginLeft: 4 }}>{k.en ?? '?'}×{k.boy ?? '?'}</span> : null}</span>
                                                    <div className="kka-kat-row-actions">
                                                        <button className="kka-icon-btn edit" title="Düzenle" onClick={() => openKatEdit(b.blokID, k)}><i className="fas fa-pen"></i></button>
                                                        <button
                                                            className="kka-icon-btn"
                                                            title="Çizim Yap"
                                                            style={{ color: cizimKat?.kat.katID === k.katID ? '#3b6bb5' : '#aaa', fontSize: '1rem' }}
                                                            onClick={() => setCizimKat(cizimKat?.kat.katID === k.katID ? null : { blok: b, kat: k })}
                                                        ><i className="fas fa-map-marker-alt"></i></button>
                                                        <button className="kka-icon-btn del" title="Sil" onClick={() => deleteKat(b.blokID, k.katID)}><i className="fas fa-trash"></i></button>
                                                    </div>
                                                </div>
                                            ))
                                        )}
                                        {/* Inline kat ekleme satırı */}
                                        {inlineKatBlokId === b.blokID ? (
                                            <div style={{ display: 'flex', gap: 6, marginTop: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <input
                                                    style={{ flex: 2, minWidth: 80, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    placeholder="Kat Adı"
                                                    value={inlineKatForm.katAdi}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, katAdi: e.target.value }))}
                                                    autoFocus
                                                />
                                                <input
                                                    style={{ width: 52, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    type="number" placeholder="Sıra"
                                                    value={inlineKatForm.siraNo}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, siraNo: e.target.value }))}
                                                />
                                                <input
                                                    style={{ width: 52, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    type="number" placeholder="En"
                                                    value={inlineKatForm.en}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, en: e.target.value }))}
                                                />
                                                <input
                                                    style={{ width: 52, fontSize: '0.82rem', padding: '4px 7px', border: '1px solid #dce', borderRadius: 5 }}
                                                    type="number" placeholder="Boy"
                                                    value={inlineKatForm.boy}
                                                    onChange={e => setInlineKatForm(f => ({ ...f, boy: e.target.value }))}
                                                />
                                                <button
                                                    style={{ fontSize: '0.8rem', padding: '4px 10px', background: '#3b82f6', color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer' }}
                                                    onClick={() => saveInlineKat(b.blokID)}
                                                    disabled={inlineKatSaving}
                                                >{inlineKatSaving ? '...' : 'Ekle'}</button>
                                                <button
                                                    style={{ fontSize: '0.8rem', padding: '4px 8px', background: '#eee', color: '#555', border: 'none', borderRadius: 5, cursor: 'pointer' }}
                                                    onClick={() => setInlineKatBlokId(null)}
                                                ><i className="fas fa-times"></i></button>
                                            </div>
                                        ) : (
                                            <button className="kka-add-kat-btn" onClick={() => openInlineKatAdd(b)}>+ Kat Ekle</button>
                                        )}
                                    </div>
                                </div>
                                </div>

                                {/* Sağ 3/4: çizim paneli */}
                                <div style={{ flex: 1, borderLeft: '2px solid #e8edf5', minHeight: 300 }}>
                                    {cizimKat ? (
                                        <KatCizimPanel
                                            key={cizimKat.kat.katID}
                                            katId={cizimKat.kat.katID}
                                            blokAdi={cizimKat.blok.blokAdi}
                                            katAdi={cizimKat.kat.katAdi}
                                            katEn={cizimKat.kat.en ?? cizimKat.blok.en}
                                            katBoy={cizimKat.kat.boy ?? cizimKat.blok.boy}
                                            blokKatlar={cizimKat.blok.katlar.map(k => ({ katID: k.katID, katAdi: k.katAdi }))}
                                            arsaId={arsaId}
                                            cepheler={arsa.cepheler.map(c => ({ cepheAdi: c.cepheAdi, yon: c.yon }))}
                                            bolumler={arsa.bolumler
                                                .filter(b => b.katID === cizimKat.kat.katID)
                                                .map(b => ({
                                                    bolumID: b.bolumID,
                                                    bolumAdi: b.bolumAdi,
                                                    xVal: b.xVal,
                                                    yVal: b.yVal,
                                                    paylasimlar: b.paylasimlar.map(p => ({
                                                        adSoyad: p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–',
                                                        oranYuzde: p.oranYuzde,
                                                    })),
                                                }))}
                                            onBolumKoordinatGuncellendi={(bolumID, xVal, yVal) => {
                                                setArsa(prev => {
                                                    if (!prev) return prev;
                                                    return {
                                                        ...prev,
                                                        bolumler: prev.bolumler.map(b =>
                                                            b.bolumID === bolumID ? { ...b, xVal, yVal } : b
                                                        )
                                                    };
                                                });
                                            }}
                                        />
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260, color: '#bbb', fontSize: '0.88rem', flexDirection: 'column', gap: 8 }}>
                                            <i className="fas fa-map-marker-alt" style={{ fontSize: '2rem', color: '#1a2340' }}></i>
                                            Kat listesinden <i className="fas fa-map-marker-alt" style={{ color: '#1a2340' }}></i> ikonuna tıklayarak çizim yapın
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}
                </div>
            )}

            {/* ── Cepheler Tab ── */}
            {activeTab === 'cepheler' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>Cepheler</h4>
                        <button className="kka-btn-primary" onClick={openCepheCreate}>+ Cephe Ekle</button>
                    </div>
                    {arsa.cepheler.length === 0 ? (
                        <div className="kka-empty">Henüz cephe eklenmemiş.</div>
                    ) : (
                        <table className="kka-sub-table">
                            <thead><tr><th>Cephe Adı</th><th>Yön</th><th>Açıklama</th><th></th></tr></thead>
                            <tbody>
                                {arsa.cepheler.map(c => (
                                    <tr key={c.cepheID}>
                                        <td><strong>{c.cepheAdi}</strong></td>
                                        <td>{c.yon ?? '–'}</td>
                                        <td>{c.aciklama ?? '–'}</td>
                                        <td>
                                            <div className="kka-td-actions">
                                                <button className="kka-icon-btn edit" onClick={() => openCepheEdit(c)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                <button className="kka-icon-btn del" onClick={() => deleteCephe(c.cepheID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* ── Bölümler Tab ── */}
            {activeTab === 'bolumler' && (
                <div className="kka-tab-section">
                    {/* Header */}
                    <div className="kka-section-header">
                        <h4>Bölümler</h4>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            {/* Görünüm toggle */}
                            <div className="kka-view-toggle">
                                <button
                                    className={`kka-view-btn${bolumView === 'liste' ? ' active' : ''}`}
                                    onClick={() => setBolumView('liste')}
                                    title="Liste görünümü"
                                ><i className="fas fa-list"></i></button>
                                <button
                                    className={`kka-view-btn${bolumView === 'tablo' ? ' active' : ''}`}
                                    onClick={() => setBolumView('tablo')}
                                    title="Tablo görünümü"
                                ><i className="fas fa-border-all"></i></button>
                            </div>
                            <button className="kka-btn-primary" onClick={openBolumCreate}>
                                <i className="fas fa-plus" style={{ marginRight: 6 }}></i>Bölüm Ekle
                            </button>
                        </div>
                    </div>

                    {arsa.bolumler.length === 0 ? (
                        <div className="kka-empty">Henüz bölüm eklenmemiş.</div>
                    ) : bolumView === 'liste' ? (
                        <>
                            {/* Filtreler */}
                            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                                <input
                                    className="kka-search-input"
                                    style={{ flex: '2 1 150px', minWidth: 120 }}
                                    placeholder="Bölüm Adı..."
                                    value={bolumFilterAdi}
                                    onChange={e => setBolumFilterAdi(e.target.value)}
                                />
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterTip}
                                    onChange={e => setBolumFilterTip(e.target.value)}
                                >
                                    <option value="">– Tüm Tipler –</option>
                                    {Array.from(new Set(arsa.bolumler.map(b => b.bolumTipi).filter((x): x is string => Boolean(x)))).map(t => (
                                        <option key={t!} value={t!}>{t}</option>
                                    ))}
                                </select>
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterTuru}
                                    onChange={e => setBolumFilterTuru(e.target.value)}
                                >
                                    <option value="">– Tüm Türler –</option>
                                    {Array.from(new Set(arsa.bolumler.map(b => b.turu).filter((x): x is string => Boolean(x)))).map(t => (
                                        <option key={t!} value={t!}>{t}</option>
                                    ))}
                                </select>
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterBlok}
                                    onChange={e => { setBolumFilterBlok(e.target.value); setBolumFilterKat(''); }}
                                >
                                    <option value="">– Tüm Bloklar –</option>
                                    {arsa.bloklar.map(b => (
                                        <option key={b.blokID} value={String(b.blokID)}>{b.blokAdi}</option>
                                    ))}
                                </select>
                                <select
                                    className="kka-filter-select"
                                    value={bolumFilterKat}
                                    onChange={e => setBolumFilterKat(e.target.value)}
                                >
                                    <option value="">– Tüm Katlar –</option>
                                    {(bolumFilterBlok
                                        ? arsa.bloklar.find(b => String(b.blokID) === bolumFilterBlok)?.katlar ?? []
                                        : arsa.bloklar.flatMap(b => b.katlar)
                                    )
                                        .sort((a, c) => a.siraNo - c.siraNo)
                                        .map(k => (
                                            <option key={k.katID} value={String(k.katID)}>{k.katAdi}</option>
                                        ))}
                                </select>
                            </div>
                            {/* Tablo */}
                            {(() => {
                                const filtered = arsa.bolumler.filter(b => {
                                    if (bolumFilterAdi && !b.bolumAdi.toLowerCase().includes(bolumFilterAdi.toLowerCase())) return false;
                                    if (bolumFilterTip && b.bolumTipi !== bolumFilterTip) return false;
                                    if (bolumFilterTuru && b.turu !== bolumFilterTuru) return false;
                                    if (bolumFilterBlok && String(b.blokID) !== bolumFilterBlok) return false;
                                    if (bolumFilterKat && String(b.katID) !== bolumFilterKat) return false;
                                    return true;
                                });
                                if (filtered.length === 0) return <div className="kka-empty">Filtreye uygun bölüm bulunamadı.</div>;
                                return (
                                    <table className="kka-sub-table">
                                        <thead>
                                            <tr>
                                                <th>Bölüm Adı</th>
                                                <th>Tip</th>
                                                <th>Türü</th>
                                                <th>Blok</th>
                                                <th>Kat</th>
                                                <th>Cepheler</th>
                                                <th>m²</th>
                                                <th>Fiyat</th>
                                                <th>Birim Fiyat</th>
                                                <th>Paylaşılan Alan</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map(b => {
                                                const paylasılan = bolumPaylasılanAlan(b);
                                                const hasPaylasim = b.paylasimlar.length > 0;
                                                return (
                                                    <tr key={b.bolumID} className={hasPaylasim ? 'kka-row-paylasimli' : ''}>
                                                        <td><strong>{b.bolumAdi}</strong></td>
                                                        <td>{b.bolumTipi ?? '–'}</td>
                                                        <td>{b.turu ?? '–'}</td>
                                                        <td>{b.blok?.blokAdi ?? '–'}</td>
                                                        <td>{b.kat?.katAdi ?? '–'}</td>
                                                        <td>
                                                            {[b.cephe1?.cepheAdi, b.cephe2?.cepheAdi, b.cephe3?.cepheAdi, b.cephe4?.cepheAdi]
                                                                .filter(Boolean).join(', ') || '–'}
                                                        </td>
                                                        <td>{fmt(b.m2)} m²</td>
                                                        <td>{fmt(b.fiyat)} ₺</td>
                                                        <td>{fmt(b.birimFiyat)} ₺</td>
                                                        <td>
                                                            {hasPaylasim ? (
                                                                <span className="kka-paylasilan-badge">
                                                                    {fmt(paylasılan)} m²
                                                                </span>
                                                            ) : <span style={{ color: '#bbb' }}>–</span>}
                                                        </td>
                                                        <td>
                                                            <div className="kka-td-actions">
                                                                <button className="kka-icon-btn edit" onClick={() => openBolumEdit(b)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                                <button className="kka-icon-btn del" onClick={() => deleteBolum(b.bolumID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </>
                    ) : (
                        /* ── Tablo Görünümü ── */
                        <>
                            <div style={{ marginBottom: 14 }}>
                                <select
                                    className="kka-filter-select"
                                    style={{ minWidth: 200 }}
                                    value={bolumTabloBlokId ?? ''}
                                    onChange={e => setBolumTabloBlokId(e.target.value ? Number(e.target.value) : null)}
                                >
                                    <option value="">-- Blok Seçin --</option>
                                    {arsa.bloklar.map(b => (
                                        <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>
                                    ))}
                                </select>
                            </div>
                            {!bolumTabloBlokId ? (
                                <div className="kka-empty">Lütfen bir blok seçin.</div>
                            ) : (() => {
                                const blok = arsa.bloklar.find(b => b.blokID === bolumTabloBlokId);
                                if (!blok) return null;
                                const katlar = [...blok.katlar].sort((a, b) => b.siraNo - a.siraNo);
                                if (katlar.length === 0) return <div className="kka-empty">Bu blokta kat tanımlı değil.</div>;
                                return (
                                    <div className="kka-bolum-tablo-wrap">
                                        {katlar.map(kat => {
                                            const bolumlerInKat = arsa.bolumler.filter(b => b.katID === kat.katID);
                                            return (
                                                <div key={kat.katID} className="kka-bolum-tablo-row">
                                                    <div className="kka-bolum-tablo-kat-label">
                                                        <span>{kat.katAdi}</span>
                                                        <button
                                                            className="kka-kat-plan-btn"
                                                            title="Kat Planını Görüntüle"
                                                            onClick={() => setPlanModalKat({ blok, kat })}
                                                        >
                                                            <i className="fas fa-map" style={{ color: '#aaa' }}></i>
                                                        </button>
                                                    </div>
                                                    <div className="kka-bolum-tablo-cells">
                                                        {bolumlerInKat.length === 0 ? (
                                                            <div className="kka-bolum-tablo-empty-kat">–</div>
                                                        ) : bolumlerInKat.map(b => {
                                                            const hasPaylasim = b.paylasimlar.length > 0;
                                                            const paylasılan = bolumPaylasılanAlan(b);
                                                            const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
                                                            const isHovered = hoveredBolumID === b.bolumID;
                                                            return (
                                                                <div
                                                                    key={b.bolumID}
                                                                    className={`kka-bolum-cell${hasPaylasim ? ' paylasimli' : ''}`}
                                                                    onMouseEnter={() => setHoveredBolumID(b.bolumID)}
                                                                    onMouseLeave={() => setHoveredBolumID(null)}
                                                                >
                                                                    <div className="kka-bolum-cell-name">{b.bolumAdi}</div>
                                                                    {b.bolumTipi && <div className="kka-bolum-cell-tip">{b.bolumTipi}</div>}
                                                                    <div className="kka-bolum-cell-m2">{fmt(b.m2)} m²</div>
                                                                    {hasPaylasim && (
                                                                        <div className="kka-bolum-cell-oran">%{toplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 1 })}</div>
                                                                    )}
                                                                    <div className="kka-bolum-cell-actions">
                                                                        <button className="kka-icon-btn edit" onClick={() => openBolumEdit(b)} title="Düzenle"><i className="fas fa-pen" style={{ fontSize: '0.7rem' }}></i></button>
                                                                        <button className="kka-icon-btn del" onClick={() => deleteBolum(b.bolumID)} title="Sil"><i className="fas fa-trash" style={{ fontSize: '0.7rem' }}></i></button>
                                                                        <button className="kka-icon-btn" onClick={() => openPaylasimCreate(b.bolumID)} title="Paylaşım Ekle" style={{ color: '#cd6906' }}><i className="fas fa-share-alt" style={{ fontSize: '0.7rem' }}></i></button>
                                                                    </div>
                                                                    {/* Hover Tooltip */}
                                                                    {isHovered && (
                                                                        <div className="kka-bolum-tooltip">
                                                                            <div className="kka-bolum-tooltip-header">
                                                                                <strong>{b.bolumAdi}</strong>
                                                                                {b.bolumTipi && <span> – {b.bolumTipi}</span>}
                                                                            </div>
                                                                            <div className="kka-bolum-tooltip-info">
                                                                                <span>{fmt(b.m2)} m²</span>
                                                                                <span>{fmt(b.fiyat)} ₺</span>
                                                                            </div>
                                                                            {b.paylasimlar.length > 0 ? (
                                                                                <>
                                                                                    <div className="kka-bolum-tooltip-paylasim-title">Paylaşımlar</div>
                                                                                    {b.paylasimlar.map(p => (
                                                                                        <div key={p.paylasimID} className="kka-bolum-tooltip-paylasim-row">
                                                                                            <span>{p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–'}</span>
                                                                                            <span>%{p.oranYuzde}</span>
                                                                                            <span>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</span>
                                                                                        </div>
                                                                                    ))}
                                                                                    <div className="kka-bolum-tooltip-toplam">
                                                                                        Paylaşılan: <strong>{fmt(paylasılan)} m²</strong>&nbsp;(%{fmt(toplamOran)})
                                                                                    </div>
                                                                                </>
                                                                            ) : (
                                                                                <div className="kka-bolum-tooltip-nopaylasim">Paylaşım yapılmamış</div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </>
                    )}
                </div>
            )}

            {/* ── Paylaşımlar Tab ── */}
            {activeTab === 'paylasimlar' && (
                <div className="kka-tab-section">
                    <div className="kka-section-header">
                        <h4>Paylaşımlar</h4>
                        <button
                            className="kka-btn-secondary"
                            onClick={fetchOneri}
                            disabled={oneriLoading}
                            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                        >
                            {oneriLoading ? <><i className="fas fa-spinner fa-spin" style={{ color: '#1a2340', marginRight: 6 }}></i>Hesaplanıyor...</> : <><i className="fas fa-lightbulb" style={{ color: '#1a2340', marginRight: 6 }}></i>Dağıtım Önerisi Hazırla</>}
                        </button>
                    </div>
                    {arsa.bolumler.length === 0 ? (
                        <div className="kka-empty">Önce Bölüm sekmesinden bölüm ekleyin.</div>
                    ) : arsa.bolumler.map(b => {
                        const toplamOran = b.paylasimlar.reduce((acc, p) => acc + p.oranYuzde, 0);
                        const tamamlandi = toplamOran >= 100;
                        const cepheler = [b.cephe1, b.cephe2, b.cephe3, b.cephe4].filter(Boolean).map(c => c!.cepheAdi).join(', ');
                        return (
                        <div key={b.bolumID} className={`kka-paylasim-bolum${tamamlandi ? ' tamamlandi' : ''}`}>
                            <div className="kka-paylasim-bolum-header">
                                <div>
                                    <div className="kka-paylasim-bolum-title">
                                        <strong>{b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}</strong>
                                        {tamamlandi && <span className="kka-paylasim-tamamlandi-badge"><i className="fas fa-check"></i> Tamamlandı</span>}
                                    </div>
                                    <div className="kka-paylasim-bolum-meta">
                                        {b.blok && <span><i className="fas fa-building"></i> {b.blok.blokAdi}</span>}
                                        {b.kat && <span><i className="fas fa-layer-group"></i> {b.kat.katAdi}</span>}
                                        {cepheler && <span><i className="fas fa-compass"></i> {cepheler}</span>}
                                        <span><i className="fas fa-ruler-combined"></i> {b.m2.toLocaleString('tr-TR')} m²</span>
                                        <span><i className="fas fa-tag"></i> {fmt(b.fiyat)} ₺</span>
                                        <span className={`kka-paylasim-oran-badge${tamamlandi ? ' tam' : toplamOran > 0 ? ' kismi' : ''}`}>%{toplamOran.toLocaleString('tr-TR')} dağıtıldı</span>
                                    </div>
                                </div>
                                <button className="kka-btn-primary" style={{ fontSize: '0.8rem', padding: '6px 12px', flexShrink: 0 }} onClick={() => openPaylasimCreate(b.bolumID)}>+ Paylaşım Ekle</button>
                            </div>
                            {b.paylasimlar.length === 0 ? (
                                <p style={{ color: '#bbb', fontSize: '0.85rem', marginLeft: 8 }}>Henüz paylaşım yok.</p>
                            ) : (
                                <table className="kka-sub-table">
                                    <thead><tr><th>Kişi / Kurum</th><th>Tür</th><th>Oran (%)</th><th>Pay (TL)</th><th>Not</th><th></th></tr></thead>
                                    <tbody>
                                        {b.paylasimlar.map(p => (
                                            <tr key={p.paylasimID}>
                                                <td><strong>{p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–'}</strong></td>
                                                <td>{p.ortak ? 'Arsa Ortağı' : p.muteahhit ? 'Müteahhit' : '–'}</td>
                                                <td>%{p.oranYuzde.toLocaleString('tr-TR')}</td>
                                                <td>{fmt(b.fiyat * p.oranYuzde / 100)} ₺</td>
                                                <td>{p.aciklamaNot ?? '–'}</td>
                                                <td>
                                                    <div className="kka-td-actions">
                                                        <button className="kka-icon-btn edit" onClick={() => openPaylasimEdit(b.bolumID, p)}><i className="fas fa-pen" style={{ color: '#1a2340' }}></i></button>
                                                        <button className="kka-icon-btn del" onClick={() => deletePaylasim(b.bolumID, p.paylasimID)}><i className="fas fa-trash" style={{ color: '#1a2340' }}></i></button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}

            {/* �?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?
                MODALLER
            �?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�?�? */}

            {/* Blok Raporu Seçim Modalı */}
            {showBlokRaporModal && arsa && (
                <div className="kka-modal-overlay" onClick={() => setShowBlokRaporModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-th-large" style={{ marginRight: 8, color: '#cd6906' }}></i>Blok Görünüm Raporu</h3>
                            <button className="kka-modal-close" onClick={() => setShowBlokRaporModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: 14 }}>Rapor alınacak bloğu seçin:</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {arsa.bloklar.map(b => (
                                    <button
                                        key={b.blokID}
                                        onClick={() => setBlokRaporBlokId(b.blokID)}
                                        style={{
                                            padding: '10px 14px', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                                            fontSize: '0.88rem', fontWeight: blokRaporBlokId === b.blokID ? 700 : 400,
                                            background: blokRaporBlokId === b.blokID ? '#eef3ff' : '#f9fafb',
                                            border: `1.5px solid ${blokRaporBlokId === b.blokID ? '#1a2340' : '#e0e4ef'}`,
                                            color: '#1a2340',
                                        }}
                                    >
                                        <i className="fas fa-layer-group" style={{ marginRight: 8, opacity: 0.55 }}></i>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setShowBlokRaporModal(false)}>İptal</button>
                            <button
                                className="kka-btn-primary"
                                disabled={blokRaporBlokId === null}
                                onClick={() => { setShowBlokRaporModal(false); exportBlokGorunumPdf(blokRaporBlokId!); }}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>PDF İndir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Blok Paylaşımlı Raporu Seçim Modalı */}
            {showBlokRaporPayliModal && arsa && (
                <div className="kka-modal-overlay" onClick={() => setShowBlokRaporPayliModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-th-large" style={{ marginRight: 8, color: '#27ae60' }}></i>Blok Görünüm Raporu (Paylaşımlı)</h3>
                            <button className="kka-modal-close" onClick={() => setShowBlokRaporPayliModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: 14 }}>Rapor alınacak bloğu seçin:</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {arsa.bloklar.map(b => (
                                    <button
                                        key={b.blokID}
                                        onClick={() => setBlokRaporPayliBlokId(b.blokID)}
                                        style={{
                                            padding: '10px 14px', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                                            fontSize: '0.88rem', fontWeight: blokRaporPayliBlokId === b.blokID ? 700 : 400,
                                            background: blokRaporPayliBlokId === b.blokID ? '#edfaf3' : '#f9fafb',
                                            border: `1.5px solid ${blokRaporPayliBlokId === b.blokID ? '#27ae60' : '#e0e4ef'}`,
                                            color: '#1a2340',
                                        }}
                                    >
                                        <i className="fas fa-layer-group" style={{ marginRight: 8, opacity: 0.55 }}></i>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setShowBlokRaporPayliModal(false)}>İptal</button>
                            <button
                                className="kka-btn-primary"
                                disabled={blokRaporPayliBlokId === null}
                                onClick={() => { setShowBlokRaporPayliModal(false); exportBlokGorunumPayliPdf(blokRaporPayliBlokId!); }}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>PDF İndir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Vaziyet Planları Blok Seçim Modalı */}
            {showVaziyetModal && arsa && (
                <div className="kka-modal-overlay" onClick={() => setShowVaziyetModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-map-marked-alt" style={{ marginRight: 8, color: '#8e44ad' }}></i>Vaziyet Planları</h3>
                            <button className="kka-modal-close" onClick={() => setShowVaziyetModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: 14 }}>Kat planları PDF'e aktarılacak bloğu seçin:</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {arsa.bloklar.map(b => (
                                    <button
                                        key={b.blokID}
                                        onClick={() => setVaziyetBlokId(b.blokID)}
                                        style={{
                                            padding: '10px 14px', borderRadius: 7, cursor: 'pointer', textAlign: 'left',
                                            fontSize: '0.88rem', fontWeight: vaziyetBlokId === b.blokID ? 700 : 400,
                                            background: vaziyetBlokId === b.blokID ? '#f5eeff' : '#f9fafb',
                                            border: `1.5px solid ${vaziyetBlokId === b.blokID ? '#8e44ad' : '#e0e4ef'}`,
                                            color: '#1a2340',
                                        }}
                                    >
                                        <i className="fas fa-layer-group" style={{ marginRight: 8, opacity: 0.55 }}></i>
                                        {b.blokAdi}{b.katSayisi ? ` (${b.katSayisi} kat)` : ''}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setShowVaziyetModal(false)}>İptal</button>
                            <button
                                className="kka-btn-primary"
                                disabled={vaziyetBlokId === null}
                                style={{ background: '#8e44ad', borderColor: '#8e44ad' }}
                                onClick={() => { setShowVaziyetModal(false); exportVaziyetPlanPdf(vaziyetBlokId!); }}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>PDF İndir
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kat Plan Modalı */}            {planModalKat && arsa && (                <div className="kka-modal-overlay" onClick={() => setPlanModalKat(null)}>
                    <div className="kka-plan-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-plan-modal-header">
                            <span><i className="fas fa-map" style={{ marginRight: 8, opacity: 0.7 }}></i>{planModalKat.blok.blokAdi} — {planModalKat.kat.katAdi}</span>
                            <button className="kka-modal-close" onClick={() => setPlanModalKat(null)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-plan-modal-body">
                            <KatCizimPanel
                                key={planModalKat.kat.katID}
                                katId={planModalKat.kat.katID}
                                blokAdi={planModalKat.blok.blokAdi}
                                katAdi={planModalKat.kat.katAdi}
                                katEn={planModalKat.kat.en ?? planModalKat.blok.en}
                                katBoy={planModalKat.kat.boy ?? planModalKat.blok.boy}
                                blokKatlar={planModalKat.blok.katlar.map(k => ({ katID: k.katID, katAdi: k.katAdi }))}
                                arsaId={arsaId}
                                cepheler={arsa.cepheler.map(c => ({ cepheAdi: c.cepheAdi, yon: c.yon }))}
                                bolumler={arsa.bolumler
                                    .filter(b => b.katID === planModalKat.kat.katID)
                                    .map(b => ({
                                        bolumID: b.bolumID,
                                        bolumAdi: b.bolumAdi,
                                        xVal: b.xVal,
                                        yVal: b.yVal,
                                        paylasimlar: b.paylasimlar.map(p => ({
                                            adSoyad: p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–',
                                            oranYuzde: p.oranYuzde,
                                        })),
                                    }))}
                                onBolumKoordinatGuncellendi={(bolumID, xVal, yVal) => {
                                    setArsa(prev => {
                                        if (!prev) return prev;
                                        return {
                                            ...prev,
                                            bolumler: prev.bolumler.map(b =>
                                                b.bolumID === bolumID ? { ...b, xVal, yVal } : b
                                            ),
                                        };
                                    });
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Müteahhit Modal */}
            {muteahhitModal && (
                <div className="kka-modal-overlay" onClick={() => setMuteahhitModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{muteahhitEdit ? 'Müteahhiti Düzenle' : 'Müteahhit Ekle'}</h3>
                            <button className="kka-modal-close" onClick={() => setMuteahhitModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Ad Soyad *</label>
                                    <input value={muteahhitForm.adSoyad} onChange={e => setMuteahhitForm(f => ({ ...f, adSoyad: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Telefon</label>
                                    <input value={muteahhitForm.telefon} onChange={e => setMuteahhitForm(f => ({ ...f, telefon: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>E-Posta</label>
                                    <input value={muteahhitForm.eposta} onChange={e => setMuteahhitForm(f => ({ ...f, eposta: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Vergi No</label>
                                    <input value={muteahhitForm.vergiNo} onChange={e => setMuteahhitForm(f => ({ ...f, vergiNo: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Hisse m²</label>
                                    <input type="number" value={muteahhitForm.hisseMetre} onChange={e => setMuteahhitForm(f => ({ ...f, hisseMetre: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Pay (%) <span style={{ fontSize: '0.78rem', color: '#888' }}>- Manuel</span></label>
                                    <input type="number" value={muteahhitForm.payYuzde} onChange={e => setMuteahhitForm(f => ({ ...f, payYuzde: e.target.value }))} />
                                </div>
                            </div>
                            {muteahhitEdit && (
                                <div className="kka-form-row">
                                    <div className="kka-form-group">
                                        <label>Toplam Pay (%) <span style={{ fontSize: '0.78rem', color: '#888' }}>- Otomatik</span></label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={`%${muteahhitEdit.toplamPayYuzde.toLocaleString('tr-TR')}`} />
                                    </div>
                                    <div className="kka-form-group">
                                        <label>Pay (TL) <span style={{ fontSize: '0.78rem', color: '#888' }}>- Otomatik</span></label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(muteahhitEdit.payTL)} />
                                    </div>
                                </div>
                            )}
                            {muteahhitEdit && muteahhitEdit.aldigiPayTL !== null && (
                                <div className="kka-form-row">
                                    <div className="kka-form-group">
                                        <label>Aldığı Pay (TL)</label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(muteahhitEdit.aldigiPayTL)} />
                                    </div>
                                </div>
                            )}
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>Not</label>
                                    <textarea value={muteahhitForm.not} onChange={e => setMuteahhitForm(f => ({ ...f, not: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setMuteahhitModal(false)}>İptal</button>
                            <button className="kka-btn-primary" onClick={saveMuteahhit} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Ortak Modal */}
            {ortakModal && (
                <div className="kka-modal-overlay" onClick={() => setOrtakModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{ortakEdit ? 'Ortağı Düzenle' : 'Ortak Ekle'}</h3>
                            <button className="kka-modal-close" onClick={() => setOrtakModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Ad Soyad *</label>
                                    <input value={ortakForm.adSoyad} onChange={e => setOrtakForm(f => ({ ...f, adSoyad: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>TC Kimlik</label>
                                    <input value={ortakForm.tcKimlik} onChange={e => setOrtakForm(f => ({ ...f, tcKimlik: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Telefon</label>
                                    <input value={ortakForm.telefon} onChange={e => setOrtakForm(f => ({ ...f, telefon: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>E-Posta</label>
                                    <input value={ortakForm.eposta} onChange={e => setOrtakForm(f => ({ ...f, eposta: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Hisse m²</label>
                                    <input
                                        type="number"
                                        placeholder={arsa.arsaMetrekare ? `Toplam: ${arsa.arsaMetrekare} m²` : 'Hisse alanı girin'}
                                        value={ortakForm.hisseMetre}
                                        onChange={e => setOrtakForm(f => ({ ...f, hisseMetre: e.target.value }))}
                                    />
                                </div>
                                <div className="kka-form-group">
                                    <label>Hisse Oranı (%)</label>
                                    <input
                                        readOnly
                                        style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }}
                                        value={
                                            ortakForm.hisseMetre !== '' && arsa.arsaMetrekare && arsa.arsaMetrekare > 0
                                                ? ((parseFloat(ortakForm.hisseMetre) / arsa.arsaMetrekare) * 100).toFixed(4)
                                                : '–'
                                        }
                                    />
                                </div>
                            </div>
                            {ortakEdit && (ortakEdit.payTL !== null || ortakEdit.aldigiPayTL !== null) && (
                                <div className="kka-form-row">
                                    <div className="kka-form-group">
                                        <label>Pay (TL)</label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(ortakEdit.payTL)} />
                                    </div>
                                    <div className="kka-form-group">
                                        <label>Aldığı Pay (TL)</label>
                                        <input readOnly style={{ background: '#f4f6fb', color: '#555', cursor: 'not-allowed' }} value={fmt(ortakEdit.aldigiPayTL)} />
                                    </div>
                                </div>
                            )}
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>Not</label>
                                    <textarea value={ortakForm.not} onChange={e => setOrtakForm(f => ({ ...f, not: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setOrtakModal(false)}>İptal</button>
                            <button className="kka-btn-primary" onClick={saveOrtak} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Blok Modal */}
            {blokModal && (
                <div className="kka-modal-overlay" onClick={() => setBlokModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{blokEdit ? 'Bloğu Düzenle' : 'Blok Ekle'}</h3>
                            <button className="kka-modal-close" onClick={() => setBlokModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Blok Adı *</label>
                                    <input value={blokForm.blokAdi} onChange={e => setBlokForm(f => ({ ...f, blokAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Kat Sayısı</label>
                                    <input type="number" value={blokForm.katSayisi} onChange={e => setBlokForm(f => ({ ...f, katSayisi: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>En (m)</label>
                                    <input type="number" value={blokForm.en} onChange={e => setBlokForm(f => ({ ...f, en: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Boy (m)</label>
                                    <input type="number" value={blokForm.boy} onChange={e => setBlokForm(f => ({ ...f, boy: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>Açıklama</label>
                                    <textarea value={blokForm.aciklama} onChange={e => setBlokForm(f => ({ ...f, aciklama: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setBlokModal(false)}>İptal</button>
                            <button className="kka-btn-primary" onClick={saveBlok} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Kat Düzenleme Modalı (sadece edit) */}
            {katModal && (
                <div className="kka-modal-overlay" onClick={() => setKatModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>Katı Düzenle</h3>
                            <button className="kka-modal-close" onClick={() => setKatModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Kat Adı *</label>
                                    <input value={katForm.katAdi} onChange={e => setKatForm(f => ({ ...f, katAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Sıra No</label>
                                    <input type="number" value={katForm.siraNo} onChange={e => setKatForm(f => ({ ...f, siraNo: e.target.value }))} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>En (m)</label>
                                    <input type="number" value={katForm.en} onChange={e => setKatForm(f => ({ ...f, en: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Boy (m)</label>
                                    <input type="number" value={katForm.boy} onChange={e => setKatForm(f => ({ ...f, boy: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setKatModal(false)}>İptal</button>
                            <button className="kka-btn-primary" onClick={saveKat} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cephe Modal */}
            {cepheModal && (
                <div className="kka-modal-overlay" onClick={() => setCepheModal(false)}>
                    <div className="kka-modal" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{cepheEdit ? 'Cepheyi Düzenle' : 'Cephe Ekle'}</h3>
                            <button className="kka-modal-close" onClick={() => setCepheModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Cephe Adı *</label>
                                    <input value={cepheForm.cepheAdi} onChange={e => setCepheForm(f => ({ ...f, cepheAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Yön</label>
                                    <select value={cepheForm.yon} onChange={e => setCepheForm(f => ({ ...f, yon: e.target.value }))}>
                                        <option value="">Seçiniz</option>
                                        <option value="Kuzey">Kuzey</option>
                                        <option value="Güney">Güney</option>
                                        <option value="Doğu">Doğu</option>
                                        <option value="Batı">Batı</option>
                                        <option value="Kuzey Doğu">Kuzey Doğu</option>
                                        <option value="Kuzey Batı">Kuzey Batı</option>
                                        <option value="Güney Doğu">Güney Doğu</option>
                                        <option value="Güney Batı">Güney Batı</option>
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>Açıklama</label>
                                    <textarea value={cepheForm.aciklama} onChange={e => setCepheForm(f => ({ ...f, aciklama: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setCepheModal(false)}>İptal</button>
                            <button className="kka-btn-primary" onClick={saveCephe} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bölüm Modal */}
            {bolumModal && (
                <div className="kka-modal-overlay" onClick={() => setBolumModal(false)}>
                    <div className="kka-modal kka-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3>{bolumEdit ? 'Bölümü Düzenle' : 'Bölüm Ekle'}</h3>
                            <button className="kka-modal-close" onClick={() => setBolumModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Bölüm Adı *</label>
                                    <input value={bolumForm.bolumAdi} onChange={e => setBolumForm(f => ({ ...f, bolumAdi: e.target.value }))} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Bölüm Tipi</label>
                                    <select value={bolumForm.bolumTipi} onChange={e => setBolumForm(f => ({ ...f, bolumTipi: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        <option>Daire</option>
                                        <option>Dükkan</option>
                                        <option>Depo</option>
                                        <option>Ofis</option>
                                        <option>Arsa Payı</option>
                                        <option>Ortak Alan</option>
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Türü</label>
                                    <input
                                        list="bolum-turu-datalist"
                                        value={bolumForm.turu}
                                        onChange={e => setBolumForm(f => ({ ...f, turu: e.target.value }))}
                                        placeholder="Örn: 1+1, 3+1, 2+1..."
                                    />
                                    <datalist id="bolum-turu-datalist">
                                        {turuListesi.map(t => <option key={t} value={t} />)}
                                    </datalist>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Blok</label>
                                    <select value={bolumForm.blokID} onChange={e => setBolumForm(f => ({ ...f, blokID: e.target.value, katID: '' }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.bloklar.map(b => <option key={b.blokID} value={b.blokID}>{b.blokAdi}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>Kat</label>
                                    <select value={bolumForm.katID} onChange={e => setBolumForm(f => ({ ...f, katID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {katsForBlok(bolumForm.blokID).map(k => <option key={k.katID} value={k.katID}>{k.katAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Cephe 1</label>
                                    <select value={bolumForm.cephe1ID} onChange={e => setBolumForm(f => ({ ...f, cephe1ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>Cephe 2</label>
                                    <select value={bolumForm.cephe2ID} onChange={e => setBolumForm(f => ({ ...f, cephe2ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Cephe 3</label>
                                    <select value={bolumForm.cephe3ID} onChange={e => setBolumForm(f => ({ ...f, cephe3ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                                <div className="kka-form-group">
                                    <label>Cephe 4</label>
                                    <select value={bolumForm.cephe4ID} onChange={e => setBolumForm(f => ({ ...f, cephe4ID: e.target.value }))}>
                                        <option value="">– Seçiniz –</option>
                                        {arsa.cepheler.map(c => <option key={c.cepheID} value={c.cepheID}>{c.cepheAdi}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Alan (m²)</label>
                                    <input type="number" value={bolumForm.m2} onChange={e => {
                                        const newM2 = e.target.value;
                                        const fiyat = parseFloat(bolumForm.fiyat) || 0;
                                        const m2 = parseFloat(newM2) || 0;
                                        setBolumForm(f => ({ ...f, m2: newM2, birimFiyat: m2 > 0 ? String(Math.round((fiyat / m2) * 100) / 100) : '' }));
                                    }} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Toplam Fiyat (₺)</label>
                                    <input type="text" inputMode="numeric"
                                        value={bolumForm.fiyat !== '' && !isNaN(parseFloat(bolumForm.fiyat)) ? parseFloat(bolumForm.fiyat).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : bolumForm.fiyat}
                                        onChange={e => {
                                            const raw = e.target.value.replace(/\./g, '').replace(',', '.');
                                            const m2 = parseFloat(bolumForm.m2) || 0;
                                            const fiyat = parseFloat(raw) || 0;
                                            setBolumForm(f => ({ ...f, fiyat: raw, birimFiyat: m2 > 0 ? String(Math.round((fiyat / m2) * 100) / 100) : '' }));
                                        }} />
                                </div>
                            </div>
                            <div className="kka-form-row">
                                <div className="kka-form-group">
                                    <label>Birim Fiyat (₺/m²)</label>
                                    <input type="text" value={bolumForm.birimFiyat !== '' && !isNaN(parseFloat(bolumForm.birimFiyat)) ? parseFloat(bolumForm.birimFiyat).toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : ''} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} />
                                </div>
                                <div className="kka-form-group">
                                    <label>Paylasılan Alan (m²)</label>
                                    <input type="number" value={bolumEdit ? bolumEdit.paylasılanAlan : 0} readOnly style={{ background: '#f5f5f5', cursor: 'not-allowed' }} />
                                </div>
                            </div>
                            <div className="kka-form-row single">
                                <div className="kka-form-group">
                                    <label>Not</label>
                                    <textarea value={bolumForm.not} onChange={e => setBolumForm(f => ({ ...f, not: e.target.value }))} />
                                </div>
                            </div>
                        </div>
                        <div className="kka-modal-footer">
                            <button className="kka-btn-secondary" onClick={() => setBolumModal(false)}>İptal</button>
                            <button className="kka-btn-primary" onClick={saveBolum} disabled={saving}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Paylaşım Modal */}
            {paylasimModal && (() => {
                const currentBolum = paylasimBolumId ? arsa.bolumler.find(b => b.bolumID === paylasimBolumId) ?? null : null;
                const selectedOrtakID = paylasimForm.ortakID ? parseInt(paylasimForm.ortakID) : null;
                const selectedMuteahhitID = paylasimForm.muteahhitID ? parseInt(paylasimForm.muteahhitID) : null;

                // Seçilen kişinin arsa toplam PayTL
                let kisiPayTL: number | null = null;
                if (selectedOrtakID) {
                    kisiPayTL = arsa.ortaklar.find(o => o.ortakID === selectedOrtakID)?.payTL ?? null;
                } else if (selectedMuteahhitID) {
                    kisiPayTL = arsa.muteahhitler.find(m => m.muteahhitID === selectedMuteahhitID)?.payTL ?? null;
                }

                // Kişinin tüm bölümlerdeki paylaşımlardan aldığı TL (mevcut edit hariç)
                let kisiAldigiTL = 0;
                arsa.bolumler.forEach(b => {
                    b.paylasimlar.forEach(p => {
                        if (paylasimEdit && p.paylasimID === paylasimEdit.paylasimID) return;
                        if (selectedOrtakID && p.ortakID === selectedOrtakID)
                            kisiAldigiTL += b.fiyat * p.oranYuzde / 100;
                        else if (selectedMuteahhitID && p.muteahhitID === selectedMuteahhitID)
                            kisiAldigiTL += b.fiyat * p.oranYuzde / 100;
                    });
                });
                const kisiKalanTL = kisiPayTL !== null ? kisiPayTL - kisiAldigiTL : null;

                // Bölümün mevcut toplam oranı (mevcut edit hariç)
                const bolumMevcutOran = currentBolum
                    ? currentBolum.paylasimlar
                        .filter(p => !paylasimEdit || p.paylasimID !== paylasimEdit.paylasimID)
                        .reduce((acc, p) => acc + p.oranYuzde, 0)
                    : 0;
                const yeniOran = parseFloat(paylasimForm.oranYuzde) || 0;
                const bolumToplamOran = bolumMevcutOran + yeniOran;
                const oranAsimi = bolumToplamOran > 100;

                const digerPaylasimlar = currentBolum
                    ? currentBolum.paylasimlar.filter(p => !paylasimEdit || p.paylasimID !== paylasimEdit.paylasimID)
                    : [];

                return (
                    <div className="kka-modal-overlay" onClick={() => setPaylasimModal(false)}>
                        <div className="kka-modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                            <div className="kka-modal-header">
                                <h3>{paylasimEdit ? 'Paylaşımı Düzenle' : 'Paylaşım Ekle'}</h3>
                                <button className="kka-modal-close" onClick={() => setPaylasimModal(false)}><i className="fas fa-times"></i></button>
                            </div>
                            <div className="kka-modal-body">
                                {modalError && <div style={{ color: '#e74c3c', marginBottom: 10, fontSize: '0.87rem' }}>{modalError}</div>}

                                {/* Bölüm Bilgisi */}
                                {currentBolum && (
                                    <div style={{ background: '#f0f4ff', borderRadius: 7, padding: '8px 14px', marginBottom: 14, fontSize: '0.87rem', display: 'flex', justifyContent: 'space-between' }}>
                                        <span><strong>{currentBolum.bolumAdi}</strong>{currentBolum.bolumTipi ? ` – ${currentBolum.bolumTipi}` : ''}</span>
                                        <span>Fiyat: <strong>{fmt(currentBolum.fiyat)} ₺</strong></span>
                                    </div>
                                )}

                                <div className="kka-form-row single">
                                    <div className="kka-form-group">
                                        <label>Arsa Ortağı</label>
                                        <select value={paylasimForm.ortakID} onChange={e => setPaylasimForm(f => ({ ...f, ortakID: e.target.value, muteahhitID: e.target.value ? '' : f.muteahhitID }))}>
                                            <option value="">– Seçiniz –</option>
                                            {arsa.ortaklar.map(o => <option key={o.ortakID} value={o.ortakID}>{o.adSoyad}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="kka-form-row single">
                                    <div className="kka-form-group">
                                        <label>Müteahhit</label>
                                        <select value={paylasimForm.muteahhitID} onChange={e => setPaylasimForm(f => ({ ...f, muteahhitID: e.target.value, ortakID: e.target.value ? '' : f.ortakID }))}>
                                            <option value="">– Seçiniz –</option>
                                            {arsa.muteahhitler.map(m => <option key={m.muteahhitID} value={m.muteahhitID}>{m.adSoyad}</option>)}
                                        </select>
                                    </div>
                                </div>

                                {/* Seçilen Kişinin Bilgileri */}
                                {(selectedOrtakID || selectedMuteahhitID) && (() => {
                                    const maxYuzdeKisi = (kisiKalanTL !== null && currentBolum && currentBolum.fiyat > 0)
                                        ? Math.min(100 - bolumMevcutOran, (kisiKalanTL / currentBolum.fiyat) * 100)
                                        : null;
                                    const maxYuzdeStr = maxYuzdeKisi !== null
                                        ? maxYuzdeKisi.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                                        : null;
                                    return (
                                        <div style={{ background: '#f8f9fb', border: '1px solid #e3e8f0', borderRadius: 7, padding: '10px 14px', marginBottom: 14, fontSize: '0.85rem' }}>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                                                <div>
                                                    <div style={{ color: '#888', marginBottom: 2 }}>Toplam Pay (TL)</div>
                                                    <div style={{ fontWeight: 600, color: '#1a2340' }}>{kisiPayTL !== null ? `${fmt(kisiPayTL)} ₺` : '–'}</div>
                                                </div>
                                                <div>
                                                    <div style={{ color: '#888', marginBottom: 2 }}>Atanmış (TL)</div>
                                                    <div style={{ fontWeight: 600, color: '#cd6906' }}>{fmt(kisiAldigiTL)} ₺</div>
                                                </div>
                                                <div>
                                                    <div style={{ color: '#888', marginBottom: 2 }}>Kalan (TL)</div>
                                                    <div style={{ fontWeight: 600, color: kisiKalanTL !== null && kisiKalanTL < 0 ? '#e74c3c' : '#27ae60' }}>
                                                        {kisiKalanTL !== null ? `${fmt(kisiKalanTL)} ₺` : '–'}
                                                    </div>
                                                </div>
                                            </div>
                                            {maxYuzdeStr !== null && (
                                                <div style={{
                                                    marginTop: 10,
                                                    padding: '7px 12px',
                                                    borderRadius: 6,
                                                    background: maxYuzdeKisi! <= 0 ? '#fff0f0' : '#f0f7ff',
                                                    border: `1px solid ${maxYuzdeKisi! <= 0 ? '#f5c6c6' : '#bdd7f7'}`,
                                                    color: maxYuzdeKisi! <= 0 ? '#e74c3c' : '#1a6bb5',
                                                    fontWeight: 600,
                                                    fontSize: '0.84rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 7
                                                }}>
                                                    <i className={`fas ${maxYuzdeKisi! <= 0 ? 'fa-times-circle' : 'fa-info-circle'}`}></i>
                                                    {maxYuzdeKisi! <= 0
                                                        ? 'Bu kişinin bu bölümden pay alacak bakiyesi kalmadı.'
                                                        : `Bu bölümden en fazla %${maxYuzdeStr} oranında pay alabilir.`}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}

                                <div className="kka-form-row">
                                    <div className="kka-form-group">
                                        <label>Oran (%)</label>
                                        <input
                                            type="number"
                                            value={paylasimForm.oranYuzde}
                                            onChange={e => setPaylasimForm(f => ({ ...f, oranYuzde: e.target.value }))}
                                            style={oranAsimi ? { borderColor: '#e74c3c' } : {}}
                                        />
                                    </div>
                                    <div className="kka-form-group">
                                        <label>Bu Bölümün Toplam Oranı</label>
                                        <input
                                            readOnly
                                            value={`%${bolumToplamOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`}
                                            style={{ background: oranAsimi ? '#fff0f0' : '#f4f6fb', color: oranAsimi ? '#e74c3c' : '#555', fontWeight: 600, cursor: 'not-allowed' }}
                                        />
                                        {oranAsimi && <small style={{ color: '#e74c3c' }}>Toplam %100'ü aşamaz!</small>}
                                    </div>
                                </div>

                                {/* Bölümdeki Diğer Paylaşımlar */}
                                {digerPaylasimlar.length > 0 && (
                                    <div style={{ marginTop: 6, marginBottom: 10 }}>
                                        <div style={{ fontSize: '0.8rem', color: '#888', marginBottom: 6 }}>Bu bölümdeki diğer paylaşımlar:</div>
                                        <table className="kka-sub-table" style={{ fontSize: '0.82rem' }}>
                                            <thead><tr><th>Ad Soyad</th><th>Tür</th><th>Oran</th><th>Pay (TL)</th></tr></thead>
                                            <tbody>
                                                {digerPaylasimlar.map(p => (
                                                    <tr key={p.paylasimID}>
                                                        <td>{p.ortak?.adSoyad ?? p.muteahhit?.adSoyad ?? '–'}</td>
                                                        <td>{p.ortak ? 'Ortak' : 'Müteahhit'}</td>
                                                        <td>%{p.oranYuzde}</td>
                                                        <td>{currentBolum ? fmt(currentBolum.fiyat * p.oranYuzde / 100) : '–'} ₺</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                <div className="kka-form-row single">
                                    <div className="kka-form-group">
                                        <label>Not</label>
                                        <textarea value={paylasimForm.aciklamaNot} onChange={e => setPaylasimForm(f => ({ ...f, aciklamaNot: e.target.value }))} />
                                    </div>
                                </div>
                            </div>
                            <div className="kka-modal-footer">
                                <button className="kka-btn-secondary" onClick={() => setPaylasimModal(false)}>İptal</button>
                                <button className="kka-btn-primary" onClick={savePaylasim} disabled={saving || oranAsimi}>{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Dağıtım Önerisi Modal */}
            {oneriModal && oneriData && (
                <div className="kka-modal-overlay" onClick={() => setOneriModal(false)}>
                    <div className="kka-modal" style={{ maxWidth: '96vw', width: '96vw', maxHeight: '90vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
                        <div className="kka-modal-header">
                            <h3><i className="fas fa-lightbulb" style={{ color: '#1a2340', marginRight: 8 }}></i>Akıllı Dağıtım Önerileri</h3>
                            <button className="kka-modal-close" onClick={() => setOneriModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="kka-modal-body">
                            <div style={{ background: '#f0f4ff', borderRadius: 8, padding: '10px 16px', marginBottom: 18, fontSize: '0.86rem', color: '#444' }}>
                                Bu rapor, her kişinin kalan hak ettiği TL tutarına göre dağıtılmamış bölüm paylarını orantısal olarak önerir.
                                Onaylanana kadar hiçbir değişiklik uygulanmaz.
                            </div>

                            {oneriData.bolumler.filter(b => b.kalanOran > 0).length === 0 ? (
                                <div className="kka-empty">Tüm bölümler %100 dağıtılmış, öneri üretilecek alan yok.</div>
                            ) : (
                                oneriData.bolumler.map(b => (
                                    <div key={b.bolumID} style={{ marginBottom: 28 }}>
                                        {/* Bölüm başlığı */}
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, borderBottom: '2px solid #e8edf5', paddingBottom: 6 }}>
                                            <strong style={{ color: '#1a2340', fontSize: '0.97rem' }}>
                                                {b.bolumAdi}{b.bolumTipi ? ` – ${b.bolumTipi}` : ''}
                                            </strong>
                                            <div style={{ display: 'flex', gap: 12, fontSize: '0.83rem' }}>
                                                <span>Fiyat: <strong>{fmt(b.fiyat)} ₺</strong></span>
                                                <span style={{ color: '#27ae60' }}>Dağıtılan: <strong>%{b.dagitilanOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</strong></span>
                                                <span style={{ color: b.kalanOran > 0 ? '#cd6906' : '#27ae60' }}>
                                                    Kalan: <strong>%{b.kalanOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</strong>
                                                </span>
                                                {b.atanmayanFiyat > 0 && (
                                                    <span style={{ color: '#c0392b', fontStyle: 'italic' }}>
                                                        Atanamayan: <strong>{fmt(b.atanmayanFiyat)} ₺</strong>
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mevcut paylaşımlar */}
                                        {b.mevcutPaylasimlar.length > 0 && (
                                            <div style={{ marginBottom: 10 }}>
                                                <div style={{ fontSize: '0.78rem', color: '#888', marginBottom: 4 }}>Mevcut paylaşımlar:</div>
                                                <table className="kka-sub-table" style={{ fontSize: '0.82rem', opacity: 0.85 }}>
                                                    <thead><tr><th>Ad Soyad</th><th>Tür</th><th>Oran</th><th>Pay (TL)</th></tr></thead>
                                                    <tbody>
                                                        {b.mevcutPaylasimlar.map(p => (
                                                            <tr key={p.paylasimID}>
                                                                <td>{p.adSoyad}</td>
                                                                <td>{p.tip === 'Ortak' ? 'Arsa Ortağı' : 'Müteahhit'}</td>
                                                                <td>%{p.oranYuzde}</td>
                                                                <td>{fmt(p.payTL)} ₺</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {/* Öneriler */}
                                        {b.kalanOran > 0 && b.oneriler.length > 0 && (
                                            <div>
                                                <div style={{ fontSize: '0.78rem', color: '#cd6906', marginBottom: 4, fontWeight: 600 }}>
                                                    <i className="fas fa-magic" style={{ marginRight: 5 }}></i>Önerilen dağıtım (%{b.kalanOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })} kalan — en yakın kalanTL'ye öncelik, %5 altı atlandı):
                                                </div>
                                                <table className="kka-sub-table" style={{ fontSize: '0.85rem' }}>
                                                    <thead>
                                                        <tr>
                                                            <th>Ad Soyad</th>
                                                            <th>Tür</th>
                                                            <th>Toplam Hak (TL)</th>
                                                            <th>Önceki Kalan (TL)</th>
                                                            <th>Öneri Oran</th>
                                                            <th>Öneri TL</th>
                                                            <th>Sonraki Kalan (TL)</th>
                                                            <th>Durum</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {b.oneriler.map(o => (
                                                            <tr key={`${o.tip}-${o.id}`}>
                                                                <td><strong>{o.adSoyad}</strong></td>
                                                                <td>{o.tip === 'Ortak' ? 'Arsa Ortağı' : 'Müteahhit'}</td>
                                                                <td>{fmt(o.payTL)} ₺</td>
                                                                <td style={{ color: '#cd6906', fontWeight: 600 }}>{fmt(o.kalanOncesi)} ₺</td>
                                                                <td style={{ fontWeight: 700, color: '#1a2340' }}>%{o.oneriOran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}</td>
                                                                <td style={{ fontWeight: 700, color: '#1a5cad' }}>{fmt(o.oneriTL)} ₺</td>
                                                                <td style={{ color: o.durum === 'tamamlandi' ? '#27ae60' : '#cd6906', fontWeight: 600 }}>{fmt(o.kalanSonra)} ₺</td>
                                                                <td>
                                                                    {o.durum === 'tamamlandi'
                                                                        ? <span style={{ color: '#27ae60' }}><i className="fas fa-check" style={{ marginRight: 4 }}></i>Tamamlandı</span>
                                                                        : <span style={{ color: '#cd6906' }}>{fmt(o.kalanSonra)} ₺ devam</span>}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}

                                        {b.kalanOran > 0 && b.oneriler.length === 0 && (
                                            <div style={{ fontSize: '0.84rem', color: '#aaa', fontStyle: 'italic' }}>
                                                Kalan pay hakkı olan kişi yok — tüm kişilerin payları tamamlanmış.
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                        {/* Atanamayanlar */}
                        {oneriData && oneriData.atanamamis && oneriData.atanamamis.length > 0 && (
                            <div style={{ margin: '12px 0 4px', padding: '12px 16px', background: '#fff3cd', borderRadius: 8, border: '1px solid #f0c040' }}>
                                <div style={{ fontWeight: 700, color: '#856404', marginBottom: 8, fontSize: '0.9rem' }}>
                                    <i className="fas fa-exclamation-triangle" style={{ marginRight: 5 }}></i>Dağıtılamayan Kişiler ({oneriData.atanamamis.length} kişi)
                                </div>
                                <table className="kka-sub-table" style={{ fontSize: '0.85rem' }}>
                                    <thead>
                                        <tr>
                                            <th>Ad Soyad</th>
                                            <th>Tür</th>
                                            <th>Toplam Hak (TL)</th>
                                            <th>Dağıtılamayan (TL)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {oneriData.atanamamis.map(a => (
                                            <tr key={`atanamamis-${a.tip}-${a.id}`}>
                                                <td><strong>{a.adSoyad}</strong></td>
                                                <td>{a.tip === 'Ortak' ? 'Arsa Ortağı' : 'Müteahhit'}</td>
                                                <td>{fmt(a.payTL)} ₺</td>
                                                <td style={{ color: '#c0392b', fontWeight: 700 }}>{fmt(a.kalanTL)} ₺</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="kka-modal-footer">
                            <button
                                className="kka-btn-primary"
                                style={{ background: '#c0392b', borderColor: '#c0392b' }}
                                onClick={exportOneriPdf}
                            >
                                <i className="fas fa-file-pdf" style={{ marginRight: 6 }}></i>PDF İndir
                            </button>
                            <button className="kka-btn-secondary" onClick={() => setOneriModal(false)}>Kapat</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KatKarsiligArsaDetay;

